import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  useMediaQuery,
  useTheme,
  Container,
  Paper,
  Grid,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Popover,
  Slider,
  InputAdornment,
  CircularProgress,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Card,
  Divider,
  Snackbar,
  Alert,
  Chip
} from '@mui/material';
import Badge from '@mui/material/Badge';
import { fetchStreetsForCity, fetchPolishCities, debounce, getPopularCities, getDistrictsForCity, normalizePolishText } from './services/streetApi';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Favorite as FavoriteIcon,
  LocationOn as LocationIcon,
  SquareFoot as SquareFootIcon,
  AttachMoney as MoneyIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  CalendarMonth as CalendarIcon,
  HomeWork as AmenitiesIcon,
  SmokingRooms as SmokingIcon,
  Pets as PetsIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  ExitToApp as ExitToAppIcon,
  HomeWork as HomeWorkIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import './App.css';
import { TAB_TENANTS, TAB_PROPERTIES } from './constants/tabs';
import PropertyCarousel from './components/PropertyCarousel';
import TenantProfileCarousel from './components/TenantProfileCarousel';
import AuthModal from './components/AuthModal';
import AddListingForm from './components/AddListingFormNew';
import PropertyDetailsModal from './components/PropertyDetailsModal';
import TenantDetailsModal from './components/TenantDetailsModal';
import RemoveFavoriteButton from './components/RemoveFavoriteButton';
import MessagesInbox, { useUnreadMessages } from './components/MessagesInbox';
import BugReportWidget from './components/BugReportWidget';
import NotificationPreferences from './components/NotificationPreferences';
import { priceReducedProperties, recentlyViewedProperties } from './data/mockProperties';
import { dataService } from './services/dataService';
import backgroundImage from './ux/sitebackgroundlow.jpg';
import logoImage from './ux/catlogo4.png';
import logoSquare from './ux/logo512.png';
import Listing from './components/Listing';
import PropertyCardSkeleton from './components/PropertyCardSkeleton';
import NotFound from './components/NotFound';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsent from './components/CookieConsent';
import PasswordGate from './components/PasswordGate';
import CloseIcon from '@mui/icons-material/Close';
import { supabase } from './services/supabaseService';
import { useAuth } from './hooks/useAuth';
import { useFavorites } from './hooks/useFavorites';
import { useEditListing } from './hooks/useEditListing';
import { useProperties } from './hooks/useProperties';
import { usePriceReduction } from './hooks/usePriceReduction';
import { AppProvider, useApp } from './contexts/AppContext';
import { useSearch } from './hooks/useSearch';
import { useShareableUrl, buildPropertyUrl, buildTenantUrl } from './hooks/useShareableUrl';

// Komponent dialogu resetu hasła (po kliknięciu linku z emaila)
function RecoveryPasswordDialog({ open, onClose }) {
  const [newPassword, setNewPassword] = React.useState('');
  const [repeatPassword, setRepeatPassword] = React.useState('');
  const [showNew, setShowNew] = React.useState(false);
  const [showRepeat, setShowRepeat] = React.useState(false);
  const [status, setStatus] = React.useState('idle');
  const [errorMsg, setErrorMsg] = React.useState('');

  const handleSubmit = async () => {
    setErrorMsg('');
    if (!newPassword || !repeatPassword) {
      setErrorMsg('Wypełnij oba pola.');
      return;
    }
    if (newPassword !== repeatPassword) {
      setErrorMsg('Hasła nie są identyczne.');
      return;
    }
    if (newPassword.length < 6) {
      setErrorMsg('Hasło musi mieć co najmniej 6 znaków.');
      return;
    }
    setStatus('loading');
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.message || 'Wystąpił błąd. Spróbuj ponownie.');
      setStatus('error');
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': { borderColor: '#D2691E' },
      '&.Mui-focused fieldset': { borderColor: '#D2691E' },
    },
    '& label.Mui-focused': { color: '#D2691E' },
  };

  return (
    <Dialog open={open} onClose={status === 'success' ? onClose : undefined} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <img src={logoSquare} alt="logo" style={{ width: 60, height: 60, borderRadius: 12 }} />
          <Typography variant="h6" sx={{ mt: 1, fontWeight: 700, color: '#1f2937' }}>
            Ustaw nowe hasło
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
            dobrynajemca.pl
          </Typography>
        </Box>

        {status === 'success' ? (
          <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#F0FFF4', border: '1px solid #9AE6B4' }}>
            <Typography variant="body1" sx={{ color: '#276749', fontWeight: 'bold' }}>
              ✅ Hasło zostało zmienione!
            </Typography>
            <Button onClick={onClose} sx={{ mt: 2, color: '#D2691E' }}>Zamknij</Button>
          </Box>
        ) : (
          <Box>
            <TextField
              fullWidth
              label="Nowe hasło"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              sx={{ mb: 2, ...inputSx }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNew(!showNew)} edge="end" size="small">
                      {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              label="Powtórz nowe hasło"
              type={showRepeat ? 'text' : 'password'}
              value={repeatPassword}
              onChange={e => setRepeatPassword(e.target.value)}
              sx={{ mb: 2, ...inputSx }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowRepeat(!showRepeat)} edge="end" size="small">
                      {showRepeat ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {errorMsg && (
              <Typography variant="body2" sx={{ color: 'error.main', mb: 2 }}>{errorMsg}</Typography>
            )}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={status === 'loading'}
              sx={{
                py: 1.5,
                backgroundColor: '#D2691E',
                '&:hover': { backgroundColor: '#B85C00' },
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2
              }}
            >
              {status === 'loading' ? 'Zapisywanie...' : 'Zapisz nowe hasło'}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Komponent do zmiany hasła bezpośrednio w serwisie
function PasswordChangeForm({ userEmail }) {
  const [expanded, setExpanded] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [repeatPassword, setRepeatPassword] = React.useState('');
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showRepeat, setShowRepeat] = React.useState(false);
  const [status, setStatus] = React.useState('idle'); // idle | loading | success | error | resetSent
  const [errorMsg, setErrorMsg] = React.useState('');

  const handleForgotPassword = async () => {
    if (!userEmail) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
      setStatus('resetSent');
    } catch (err) {
      setErrorMsg(err.message || 'Nie udało się wysłać linku. Spróbuj ponownie.');
      setStatus('error');
    }
  };

  const handleCancel = () => {
    setExpanded(false);
    setCurrentPassword('');
    setNewPassword('');
    setRepeatPassword('');
    setErrorMsg('');
    setStatus('idle');
  };

  const handleChange = async () => {
    setErrorMsg('');
    if (!currentPassword || !newPassword || !repeatPassword) {
      setErrorMsg('Wypełnij wszystkie pola.');
      return;
    }
    // Weryfikacja aktualnego hasła — jako pierwsza, przed sprawdzeniem nowego
    setStatus('loading');
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password: currentPassword,
      });
      if (signInError) {
        setErrorMsg('Aktualne hasło jest nieprawidłowe.');
        setStatus('error');
        return;
      }
    } catch (err) {
      setErrorMsg('Błąd weryfikacji hasła. Spróbuj ponownie.');
      setStatus('error');
      return;
    }
    // Walidacja nowego hasła dopiero po potwierdzeniu aktualnego
    if (newPassword !== repeatPassword) {
      setErrorMsg('Nowe hasła nie są identyczne.');
      setStatus('error');
      return;
    }
    if (newPassword.length < 6) {
      setErrorMsg('Nowe hasło musi mieć co najmniej 6 znaków.');
      setStatus('error');
      return;
    }
    // Zmiana hasła
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;
      setStatus('success');
      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
    } catch (err) {
      setErrorMsg(err.message || 'Wystąpił błąd. Spróbuj ponownie.');
      setStatus('error');
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': { borderColor: '#D2691E' },
      '&.Mui-focused fieldset': { borderColor: '#D2691E' },
    },
    '& label.Mui-focused': { color: '#D2691E' },
  };

  if (status === 'success') {
    return (
      <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#F0FFF4', border: '1px solid #9AE6B4', textAlign: 'center' }}>
        <Typography variant="body1" sx={{ color: '#276749', fontWeight: 'bold' }}>
          ✅ Hasło zostało zmienione!
        </Typography>
      </Box>
    );
  }

  if (status === 'resetSent') {
    return (
      <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#EFF6FF', border: '1px solid #93C5FD', textAlign: 'center' }}>
        <Typography variant="body1" sx={{ color: '#1E40AF', fontWeight: 'bold' }}>
          📧 Link do resetu hasła został wysłany na {userEmail}
        </Typography>
        <Typography variant="body2" sx={{ color: '#3B82F6', mt: 1 }}>
          Sprawdź skrzynkę pocztową (również folder spam).
        </Typography>
      </Box>
    );
  }

  if (!expanded) {
    return (
      <Button
        variant="outlined"
        fullWidth
        sx={{
          py: 2,
          textTransform: 'none',
          fontSize: '16px',
          borderColor: '#D2691E',
          color: '#D2691E',
          '&:hover': { backgroundColor: 'rgba(210, 105, 30, 0.08)', borderColor: '#D2691E' },
        }}
        onClick={() => setExpanded(true)}
      >
        Zmień hasło
      </Button>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Aktualne hasło"
        type={showCurrent ? 'text' : 'password'}
        value={currentPassword}
        onChange={e => setCurrentPassword(e.target.value)}
        fullWidth size="small" sx={inputSx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setShowCurrent(v => !v)} edge="end">
                {showCurrent ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      <Typography
        variant="body2"
        onClick={handleForgotPassword}
        sx={{
          color: '#D2691E',
          fontSize: '12px',
          cursor: 'pointer',
          mt: -1.5,
          '&:hover': { textDecoration: 'underline' }
        }}
      >
        Zapomniałem hasła
      </Typography>
      <TextField
        label="Nowe hasło"
        type={showNew ? 'text' : 'password'}
        value={newPassword}
        onChange={e => setNewPassword(e.target.value)}
        fullWidth size="small" sx={inputSx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setShowNew(v => !v)} edge="end">
                {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      <TextField
        label="Powtórz nowe hasło"
        type={showRepeat ? 'text' : 'password'}
        value={repeatPassword}
        onChange={e => setRepeatPassword(e.target.value)}
        fullWidth size="small" sx={inputSx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setShowRepeat(v => !v)} edge="end">
                {showRepeat ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      {errorMsg && (
        <Typography variant="body2" sx={{ color: '#EF4444', mt: -1 }}>
          {errorMsg}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          fullWidth
          sx={{
            py: 1.5,
            textTransform: 'none',
            fontSize: '16px',
            borderColor: '#999',
            color: '#666',
            '&:hover': { borderColor: '#555', backgroundColor: 'rgba(0,0,0,0.04)' },
          }}
          onClick={handleCancel}
        >
          Anuluj
        </Button>
        <Button
          variant="contained"
          fullWidth
          disabled={status === 'loading'}
          sx={{
            py: 1.5,
            textTransform: 'none',
            fontSize: '16px',
            backgroundColor: '#D2691E',
            '&:hover': { backgroundColor: '#B85C00' },
            '&.Mui-disabled': { backgroundColor: '#e0a070', color: 'white' }
          }}
          onClick={handleChange}
        >
          {status === 'loading' ? 'Zmienianie...' : 'Zmień hasło'}
        </Button>
      </Box>
    </Box>
  );
}


function AppContent() {
  const { state, actions } = useApp();
  const {
    recentProperties,
    searchResults,
    recentTenantProfiles,
    verifiedTenantProfiles,
    premiumTenantProfiles,
    dataLoading,
    selectedTab,
    propertyDetailsOpen,
    selectedProperty,
    tenantDetailsOpen,
    selectedTenant,
    searchFilters
  } = state;

  const {
    setDataLoading,
    setSearchResults,
    setRecentProperties,
    setRecentTenantProfiles,
    setVerifiedTenantProfiles,
    setPremiumTenantProfiles,
    setSelectedTab,
    setSearchFilters,
    updateSearchFilter,
    handlePropertyClick,
    handleTenantClick,
    closePropertyDetails,
    closeTenantDetails
  } = actions;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [addListingFormOpen, setAddListingFormOpen] = useState(false);
  const [myListingsOpen, setMyListingsOpen] = useState(false);
  const [editOrigin, setEditOrigin] = useState(null);
  const [editedPropertyId, setEditedPropertyId] = useState(null);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [tenantDetailsIsOwner, setTenantDetailsIsOwner] = useState(false);

  // Osobne snapshoty filtrów dla każdej zakładki — zapobiega przenoszeniu wartości między tabami
  const defaultFilters = {
    city: '', district: '', street: '', rooms: [],
    priceRange: { min: '', max: '' }, sizeRange: { min: '', max: '' },
    amenities: [], maxPersons: null, dateAdded: null,
    smoking: 'nie', nonSmoking: false, noPets: false
  };
  const [propertyFiltersSnapshot, setPropertyFiltersSnapshot] = useState(defaultFilters);
  const [tenantFiltersSnapshot, setTenantFiltersSnapshot] = useState(defaultFilters);
  const [tenantEditProfileData, setTenantEditProfileData] = useState(null);
  const [deletingPropertyId, setDeletingPropertyId] = useState(null);
  const [deletingTenantId, setDeletingTenantId] = useState(null);
  const [priceCleared, setPriceCleared] = useState(false);
  const [sizeCleared, setSizeCleared] = useState(false);
  const [roomsTouched, setRoomsTouched] = useState(false);
  const [dateTouched, setDateTouched] = useState(false);
  const [amenitiesTouched, setAmenitiesTouched] = useState(false);

  const properties = useProperties({ setRecentProperties });
  const { userListings, setUserListings, reducedProperties, setReducedProperties,
    fetchInitialProperties, fetchReducedProperties,
    fetchUserListings, handleDeleteListing,
    userTenantProfiles, setUserTenantProfiles, fetchUserTenantProfiles } = properties;

  const { registerPriceChange } = usePriceReduction();

  // Stable reference to full city list — built once, same array on every render
  const allCityOptions = useMemo(() => getPopularCities(''), []);

  // Use the search hook for all search/filter functionality
  const search = useSearch();
  const {
    citySuggestions,
    isLoadingCities,
    cityInputValue,
    districtSuggestions,
    isLoadingDistricts,
    districtInputValue,
    streetSuggestions,
    isLoadingStreets,
    streetInputValue,
    displayCity,
    displayDistrict,
    anchorEls,
    priceOptions,
    sizeOptions,
    availableAmenities,
    setCityInputValue,
    setDistrictInputValue,
    setStreetInputValue,
    setDisplayCity,
    setDisplayDistrict,
    setDistrictSuggestions,
    setStreetSuggestions,
    handleCityInputChange,
    handleCityChangeForFilters,
    handleDistrictInputChange,
    handleDistrictChange,
    handleStreetInputChange,
    handleStreetChange,
    handlePopoverOpen,
    handlePopoverClose,
    autocompletePriceValue,
    autocompleteSizeValue,
    executeSearch
  } = search;

  // Filter cities locally based on input — startsWith only, no MUI fallback
  const filteredCityOptions = useMemo(() => {
    if (!cityInputValue || cityInputValue.length === 0) return allCityOptions.slice(0, 10);
    const q = normalizePolishText(cityInputValue);
    return allCityOptions.filter(opt => normalizePolishText(opt?.name || '').startsWith(q));
  }, [cityInputValue, allCityOptions]);

  // Use the authentication hook
  const auth = useAuth();
  
  // Use the edit listing hook
  const editListing = useEditListing();

  // Ulubieni najemcy (osobny hook)
  const tenantFavorites = useFavorites(auth.userId);
  
  // Wiadomości — hook do liczenia nieprzeczytanych
  const unreadMessages = useUnreadMessages(auth.userEmail, auth.isUserLoggedIn);
  const [messagesInboxOpen, setMessagesInboxOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [welcomeSnackbarOpen, setWelcomeSnackbarOpen] = useState(false);

  // Obsługa URL ?wiadomosci — po kliknięciu przycisku w emailu powiadomień
  React.useEffect(() => {
    if (new URLSearchParams(window.location.search).has('wiadomosci')) {
      // Usuń param z URL bez przeładowania
      window.history.replaceState({}, '', window.location.pathname);
      if (auth.isUserLoggedIn) {
        setTimeout(() => setMessagesInboxOpen(true), 500);
      } else {
        // Niezalogowany — zapisz intent i otwórz modal logowania
        localStorage.setItem('pendingIntent', 'openMessages');
        setTimeout(() => {
          auth.setLoginIntent('openMessages');
          auth.setAuthModalOpen(true);
        }, 300);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const {
    isUserLoggedIn,
    userEmail,
    userId,
    userMenuAnchor,
    authModalOpen,
    loginIntent,
    settingsModalOpen,
    isRecoveryMode,
    deleteConfirmOpen,
    deleteSuccessOpen,
    favoriteProperties,
    favoritesCount,
    setIsUserLoggedIn,
    setUserEmail,
    setUserId,
    setUserMenuAnchor,
    setAuthModalOpen,
    setLoginIntent,
    setSettingsModalOpen,
    setIsRecoveryMode,
    setDeleteConfirmOpen,
    setDeleteSuccessOpen,
    setFavoriteProperties,
    setFavoritesCount,
    handleAuthSuccess,
    fetchFavoriteProperties,
    handleLogout
  } = auth;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const addButtonLabel = selectedTab === TAB_TENANTS ? 'Dodaj profil' : 'Dodaj ogłoszenie';
  // Zalogowany użytkownik z istniejącym profilem najemcy: zamiast "Dodaj profil" pokazujemy "Mój profil"
  const showMyProfileButton = selectedTab === TAB_TENANTS && !!userId && userTenantProfiles.length > 0;
  const headerButtonLabel = showMyProfileButton ? 'Mój profil' : addButtonLabel;
  const openMyTenantProfile = () => {
    setTenantDetailsIsOwner(true);
    handleTenantClickWithUrl(userTenantProfiles[0]);
  };

  // --- OSTATNIO OGLĄDANE ---
  const [viewedProperties, setViewedProperties] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wynajem_viewed') || '[]'); }
    catch { return []; }
  });

  // --- OSTATNIO OGLĄDANI NAJEMCY ---
  const [viewedTenants, setViewedTenants] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wynajem_viewed_tenants') || '[]'); }
    catch { return []; }
  });

  // Odfiltrowuj usunięte ogłoszenia z "ostatnio oglądane"
  // gdy załadują się aktualne dane z bazy
  useEffect(() => {
    if (recentProperties.length === 0) return;
    const activeIds = new Set(recentProperties.map(p => p.id));
    setViewedProperties(prev => {
      const filtered = prev.filter(p => activeIds.has(p.id));
      if (filtered.length !== prev.length) {
        localStorage.setItem('wynajem_viewed', JSON.stringify(filtered));
      }
      return filtered;
    });
  }, [recentProperties]);

  // Odfiltrowuj usunięte profile z "ostatnio oglądani najemcy"
  // gdy załadują się aktualne dane z bazy
  useEffect(() => {
    if (recentTenantProfiles.length === 0) return;
    const activeIds = new Set(recentTenantProfiles.map(t => t.id));
    setViewedTenants(prev => {
      const filtered = prev.filter(t => activeIds.has(t.id));
      if (filtered.length !== prev.length) {
        localStorage.setItem('wynajem_viewed_tenants', JSON.stringify(filtered));
      }
      return filtered;
    });
  }, [recentTenantProfiles]);

  const handlePropertyClickWithTracking = useCallback((property) => {
    handlePropertyClick(property);
    // Push shareable URL + update title/canonical
    if (property?.id) {
      const url = buildPropertyUrl(property);
      if (window.location.pathname !== url) {
        window.history.pushState({ type: 'property', id: property.id }, '', url);
        updateCanonical(url);
      }
      if (property.title) {
        const city = property.city ? ` — ${property.city}` : '';
        document.title = `${property.title}${city} | dobrynajemca.pl`;
      }
    }
    // Increment views (unique per user — localStorage + Supabase dedup)
    if (property.id) {
      const stored = JSON.parse(localStorage.getItem('wynajem_views_tracked') || '[]');
      if (!stored.includes(property.id)) {
        // Check Supabase for logged-in users
        const trackView = async () => {
          if (userId) {
            const { data } = await supabase.from('property_view_events')
              .select('id').eq('property_id', property.id).eq('user_id', userId).limit(1);
            if (data && data.length > 0) {
              // Already tracked in DB — just save to localStorage
              stored.push(property.id);
              localStorage.setItem('wynajem_views_tracked', JSON.stringify(stored));
              return;
            }
          }
          // New unique view — record event and increment counter
          try {
            await supabase.from('property_view_events').insert({ property_id: property.id, user_id: userId || null });
            await supabase.rpc('increment_views', { prop_id: property.id }).then(({ error }) => {
              if (error) {
                supabase.from('properties').update({ views: (property.views || 0) + 1 }).eq('id', property.id);
              }
            });
          } catch (e) {}
          stored.push(property.id);
          localStorage.setItem('wynajem_views_tracked', JSON.stringify(stored));
        };
        trackView();
      }
    }
    setViewedProperties(prev => {
      const filtered = prev.filter(p => p.id !== property.id);
      const updated = [property, ...filtered].slice(0, 10);
      localStorage.setItem('wynajem_viewed', JSON.stringify(updated));
      return updated;
    });
  }, [handlePropertyClick, userId]);

  // --- SHAREABLE URLs ---
  const handleTenantClickWithUrl = useCallback((tenant) => {
    handleTenantClick(tenant);
    if (tenant?.id) {
      const url = buildTenantUrl(tenant);
      if (window.location.pathname !== url) {
        window.history.pushState({ type: 'tenant', id: tenant.id }, '', url);
        updateCanonical(url);
      }
      const name = tenant.first_name || tenant.name || 'Profil najemcy';
      document.title = `${name} — profil najemcy | dobrynajemca.pl`;
    }
    // Increment tenant profile views (unique per user — localStorage + Supabase dedup)
    if (tenant?.id) {
      const stored = JSON.parse(localStorage.getItem('wynajem_tenant_views_tracked') || '[]');
      if (!stored.includes(tenant.id)) {
        const trackView = async () => {
          if (userId) {
            const { data, error: selErr } = await supabase.from('tenant_profile_view_events')
              .select('id').eq('tenant_profile_id', tenant.id).eq('user_id', userId).limit(1);
            if (selErr) console.error('[TenantViews] select error:', selErr);
            if (!selErr && data && data.length > 0) {
              stored.push(tenant.id);
              localStorage.setItem('wynajem_tenant_views_tracked', JSON.stringify(stored));
              return;
            }
          }
          // New unique view — insert event and increment counter
          const { error: insertErr } = await supabase.from('tenant_profile_view_events')
            .insert({ tenant_profile_id: tenant.id, user_id: userId || null });
          if (insertErr) {
            console.error('[TenantViews] insert error:', insertErr);
            return; // Don't cache if insert failed
          }
          const { error: rpcErr } = await supabase.rpc('increment_tenant_views', { profile_id: tenant.id });
          if (rpcErr) {
            console.error('[TenantViews] RPC error, fallback update:', rpcErr);
            const { error: updErr } = await supabase.from('tenant_profiles')
              .update({ views_count: (tenant.viewsCount || 0) + 1 }).eq('id', tenant.id);
            if (updErr) console.error('[TenantViews] fallback update error:', updErr);
          }
          stored.push(tenant.id);
          localStorage.setItem('wynajem_tenant_views_tracked', JSON.stringify(stored));
        };
        trackView();
      }
    }
    setViewedTenants(prev => {
      const filtered = prev.filter(t => t.id !== tenant.id);
      const updated = [tenant, ...filtered].slice(0, 10);
      localStorage.setItem('wynajem_viewed_tenants', JSON.stringify(updated));
      return updated;
    });
  }, [handleTenantClick, userId]);

  const shareableUrl = useShareableUrl({
    onOpenProperty: (property) => handlePropertyClickWithTracking(property),
    onOpenTenant: (tenant) => handleTenantClickWithUrl(tenant),
    onClose: () => {
      closePropertyDetails();
      closeTenantDetails();
    }
  });

  // --- MODAL ULUBIONYCH ---
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const openedFromFavoritesRef = useRef(false);
  const openedFromMyListingsRef = useRef(false);

  const handleFavoritesClose = () => {
    setFavoritesOpen(false);
  };

  const handleFavoritesClick = async () => {
    if (!isUserLoggedIn) {
      setLoginIntent('favorites');
      setAuthModalOpen(true);
      return;
    }
    await fetchFavoriteProperties();
    await tenantFavorites.fetchFavoriteTenants();
    setFavoritesOpen(true);
  };

  // Street autocomplete state (keep this as it's still used locally)
  useEffect(() => {
    // ─── Stale-while-revalidate cache ────────────────────────────────────────
    // Klucze i TTL (10 min). Po tym czasie cache jest "przestarzały" i spinner
    // pojawia się ponownie przy następnej wizycie bez danych.
    const PROPS_CACHE_KEY   = 'dn_props_v2';
    const TENANTS_CACHE_KEY = 'dn_tenants_v1';
    const CACHE_TTL         = 10 * 60 * 1000; // 10 minut

    const mapProp = (p) => ({
      id: p.id,
      image_url: Array.isArray(p.image_url) ? p.image_url : (typeof p.image_url === 'string' && p.image_url ? [p.image_url] : []),
      title: p.title || `${p.city || ''} ${p.district || ''}`.trim(),
      price: p.price || 0,
      priceString: p.price ? `${p.price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} zł/mies.` : '',
      area: p.area || '',
      rooms: p.rooms != null ? p.rooms : '',
      floor: p.floor || '',
      total_floors: p.total_floors || '',
      address: [p.street, p.building_number, p.city, p.district].filter(Boolean).join(', '),
      parking: p.amenities ? p.amenities.includes('Parking') : false,
      rent: p.rent || '',
      wynajem: p.price || '',
      city: p.city || '',
      street: p.street || '',
      district: p.district || '',
      amenities: p.amenities || [],
      equipment: p.equipment || [],
      description: p.description || '',
      deposit: p.deposit || '',
      available_from: p.available_from || '',
      building_number: p.building_number || '',
      contact: { name: p.first_name || '', phone: p.phone || '', email: p.email || '' },
      user_id: p.user_id || '',
      phone_reveals: p.phone_reveals || 0,
      views: p.views || 0,
      previous_price: p.previous_price || null,
      price_reduced_at: p.price_reduced_at || null,
      is_premium: p.is_premium || false,
      premium_until: p.premium_until || null,
      lat: p.lat || null,
      lng: p.lng || null,
      published: p.created_at ? new Date(p.created_at).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null
    });

    const loadInitialData = async () => {
      // ─── Faza 0: cache localStorage ────────────────────────────────────────
      // Jeśli są świeże dane z poprzedniej wizyty – pokaż od razu (0ms opóźnienia)
      let hasCachedData = false;
      try {
        const rawProps = localStorage.getItem(PROPS_CACHE_KEY);
        if (rawProps) {
          const { ts, data } = JSON.parse(rawProps);
          if (Date.now() - ts < CACHE_TTL) {
            setRecentProperties(data);
            hasCachedData = true;
          }
        }
        const rawTenants = localStorage.getItem(TENANTS_CACHE_KEY);
        if (rawTenants) {
          const { ts, data } = JSON.parse(rawTenants);
          if (Date.now() - ts < CACHE_TTL) {
            setRecentTenantProfiles(data);
            setVerifiedTenantProfiles(data.filter(t => t.isVerified));
            setPremiumTenantProfiles(data.slice(0, 10));
          }
        }
      } catch {}

      // Skeleton tylko gdy nie ma danych z cache
      if (!hasCachedData) setDataLoading(true);

      try {
        const { propertiesService, tenantsService } = await import('./services/supabaseService');

        // ─── Faza 1: priorytetowa – równolegle ────────────────────────────
        // getFirstN(4) + fetchReducedProperties + pierwsza strona najemców
        // lecą jednocześnie. Skeleton znika gdy wszystkie trzy wrócą.
        const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
        const [first4, reducedRaw] = await Promise.all([
          propertiesService.getFirstN(4),
          supabase
            .from('properties')
            .select('*')
            .not('previous_price', 'is', null)
            .not('price_reduced_at', 'is', null)
            .gt('price_reduced_at', fourteenDaysAgo)
            .order('price_reduced_at', { ascending: false }),
        ]);

        if (first4.length > 0) {
          setRecentProperties(first4.map(mapProp));
        }
        // Zaktualizuj reducedProperties bezpośrednio – nie czekaj na hook
        if (!reducedRaw.error) {
          const mappedReduced = (reducedRaw.data || [])
            .filter(p => p.previous_price > p.price)
            .map(p => ({ ...mapProp(p), previous_price: p.previous_price, price_reduced_at: p.price_reduced_at }));
          // Przekaż do hooka przez setReducedProperties (dostępny z useProperties)
          properties.setReducedProperties(mappedReduced);
        }
        setDataLoading(false); // ← skeleton znika, karty i obniżki już widoczne

        // ─── Faza 2: reszta danych w tle ──────────────────────────────────
        const [allData, tenants] = await Promise.all([
          propertiesService.getAll(),
          tenantsService.getAll(),
        ]);

        const mapped = (Array.isArray(allData) ? allData : []).map(mapProp);
        setRecentProperties(mapped);
        try { localStorage.setItem(PROPS_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: mapped })); } catch {}

        setRecentTenantProfiles(tenants);
        setVerifiedTenantProfiles(tenants.filter(t => t.isVerified));
        setPremiumTenantProfiles(tenants.slice(0, 10));
        try { localStorage.setItem(TENANTS_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: tenants })); } catch {}

      } catch (error) {
        // Nie czyść recentProperties przy błędzie – stale cache lepszy niż nic
      } finally {
        setDataLoading(false);
      }
    };
    loadInitialData();
  }, [setDataLoading, setRecentProperties, setRecentTenantProfiles, setVerifiedTenantProfiles, setPremiumTenantProfiles]);

  useEffect(() => {
    // Update display states when searchFilters change
    setDisplayCity(searchFilters.city || '');
    setDisplayDistrict(searchFilters.district || '');
  }, [searchFilters, setDisplayCity, setDisplayDistrict]);

  // Powrót do właściwego widoku po zamknięciu formularza edycji
  useEffect(() => {
    if (!addListingFormOpen && editOrigin) {
      if (editOrigin === 'thumbnails') {
        setMyListingsOpen(true);
      } else if (editOrigin === 'details' && editedPropertyId) {
        const updated = userListings.find(p => p.id === editedPropertyId);
        if (updated) handlePropertyClick(updated);
      }
      setEditOrigin(null);
      setEditedPropertyId(null);
    }
  }, [addListingFormOpen]);
  
  // Street autocomplete state (keep this as it's still used locally)

  // Menu items - commented out for now, may be used in the future
  // const menuItems = ['Home', 'About', 'Service', 'Blog', 'Contact'];
  const menuItems = []; // Temporarily empty

  const handleFilterChange = (field, value) => {
    
    updateSearchFilter(field, value);
    
    // Handle UI state updates that are not in context
    if (field === 'city') {
      setStreetSuggestions([]);
      setStreetInputValue('');
      setDistrictInputValue('');
      setCityInputValue(value);
    }
    
  };

  const handleRoomToggle = (roomCount) => {
    const currentRooms = searchFilters.rooms || [];
    const newRooms = currentRooms.includes(roomCount)
      ? currentRooms.filter(count => count !== roomCount)
      : [...currentRooms, roomCount];
    updateSearchFilter('rooms', newRooms);
  };

  const handleAmenityToggle = (amenity) => {
    const currentAmenities = searchFilters.amenities || [];
    const newAmenities = currentAmenities.includes(amenity)
      ? currentAmenities.filter(item => item !== amenity)
      : [...currentAmenities, amenity];
    updateSearchFilter('amenities', newAmenities);
  };

  const handlePriceInputChange = (field, value) => {
    const autocompleted = autocompletePriceValue(value);
    const newPriceRange = {
      ...searchFilters.priceRange,
      [field]: autocompleted
    };
    updateSearchFilter('priceRange', newPriceRange);
  };

  const handleSizeInputChange = (field, value) => {
    const autocompleted = autocompleteSizeValue(value);
    const newSizeRange = {
      ...searchFilters.sizeRange,
      [field]: autocompleted
    };
    updateSearchFilter('sizeRange', newSizeRange);
  };

  const handleSearch = async () => {
    if (selectedTab === TAB_TENANTS) {
      // Szukaj najemców
      // Użyj cityInputValue jako fallback jeśli użytkownik wpisał miasto bez kliknięcia sugestii
      const cityValue = searchFilters.city || cityInputValue;
      setDataLoading(true);
      try {
        const { tenantsService } = await import('./services/supabaseService');
        const results = await tenantsService.getFiltered({
          city: cityValue,
          district: searchFilters.district || districtInputValue,
          rooms: searchFilters.rooms,
          nonSmoking: searchFilters.nonSmoking,
          noPets: searchFilters.noPets,
        });
        setSearchResults(results || []);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setDataLoading(false);
      }
    } else {
      await executeSearch(searchFilters, setSearchResults, setDataLoading);
    }
  };

  // Wrapper functions to pass updateSearchFilter to hook handlers
  const wrappedHandleCityChange = (event, newValue) => {
    handleCityChangeForFilters(event, newValue, updateSearchFilter);
  };

  const wrappedHandleDistrictChange = (event, newValue) => {
    handleDistrictChange(event, newValue, updateSearchFilter);
  };

  const wrappedHandleStreetChange = (event, newValue) => {
    handleStreetChange(event, newValue, updateSearchFilter);
  };

  const wrappedHandleStreetInputChange = (event, newInputValue) => {
    handleStreetInputChange(event, newInputValue, searchFilters.city);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const fetchInitialTenants = React.useCallback(async () => {
    try {
      const tenants = await import('./services/supabaseService').then(m => m.tenantsService.getAll());
      setRecentTenantProfiles(tenants);
      setVerifiedTenantProfiles(tenants.filter(t => t.isVerified));
      setPremiumTenantProfiles(tenants.slice(0, 10));
    } catch (err) {
      setRecentTenantProfiles([]);
      setVerifiedTenantProfiles([]);
      setPremiumTenantProfiles([]);
    }
  }, []);

  // Usuwanie konta użytkownika
  const handleDeleteAccount = async ({
    fetchInitialProperties: fetchProps = fetchInitialProperties,
    fetchInitialTenants: fetchTenants = fetchInitialTenants,
    setRecentProperties: setProps = setRecentProperties,
    setRecentTenantProfiles: setTenants = setRecentTenantProfiles,
    setVerifiedTenantProfiles: setVerified = setVerifiedTenantProfiles,
    setPremiumTenantProfiles: setPremium = setPremiumTenantProfiles,
  } = {}) => {
    try {
      // Usuń ogłoszenia użytkownika po user_id
      const { error: propErr1 } = await supabase
        .from('properties').delete().eq('user_id', userId);
      if (propErr1) {}

      // Usuń stare ogłoszenia po email (user_id null)
      const { error: propErr2 } = await supabase
        .from('properties').delete().eq('email', userEmail).is('user_id', null);
      if (propErr2) {}

      // Usuń profile najemców
      const { error: tenantErr } = await supabase
        .from('tenant_profiles').delete().eq('user_id', userId);
      if (tenantErr) {}

      // Soft-delete konta
      const { error: userErr } = await supabase
        .from('users')
        .update({ is_active: false, deleted_at: new Date().toISOString() })
        .eq('id', userId);
      if (userErr) throw userErr;

      await supabase.auth.signOut();

      // Wyczyść stan
      setIsUserLoggedIn(false);
      setUserEmail('');
      setUserId(null);
      setFavoriteProperties([]);
      setUserListings([]);
      setUserTenantProfiles([]);
      setSettingsModalOpen(false);
      setDeleteConfirmOpen(false);
      setProps([]);
      setTenants([]);
      setVerified([]);
      setPremium([]);
      setSearchResults(null);

      await new Promise(r => setTimeout(r, 500));
      await Promise.all([fetchProps(), fetchTenants()]);

      setDeleteSuccessOpen(true);
    } catch (err) {
      alert(`Wystąpił błąd podczas usuwania konta: ${err.message}`);
    }
  };

  const handlePropertyAdded = React.useCallback(async () => {
    await fetchInitialProperties();
  }, [fetchInitialProperties]);

  const handleTenantAdded = React.useCallback(async () => {
    await fetchInitialTenants();
    await fetchUserTenantProfiles(userId);
    setSelectedTab(TAB_TENANTS);
  }, [fetchInitialTenants, fetchUserTenantProfiles, userId, setSelectedTab]);

  React.useEffect(() => {
    fetchInitialProperties();
    fetchInitialTenants();
    fetchReducedProperties();
  }, [fetchInitialProperties, fetchInitialTenants, fetchReducedProperties]);

  // Usuwanie apartamentu z ulubionych
  const handleRemoveFavorite = async (propertyId) => {
    if (!userId) return;
    try {
      const newFavorites = favoriteProperties.filter(p => p.id !== propertyId).map(p => p.id);
      const { error } = await supabase
        .from('users')
        .update({ favorites: newFavorites })
        .eq('id', userId);
      if (error) throw error;
      setFavoriteProperties(favoriteProperties.filter(p => p.id !== propertyId));
    } catch (err) {
    }
  };

  // Pobieranie ulubionych po zalogowaniu
  React.useEffect(() => {
    const fetchFavoritesOnLogin = async () => {
      if (!userId) {
        setFavoritesCount(0);
        setFavoriteProperties([]);
        return;
      }
      try {
        const { data: userData, error } = await supabase
          .from('users')
          .select('favorites')
          .eq('id', userId)
          .single();
        if (error) throw error;
        const favoriteIds = userData?.favorites || [];
        if (favoriteIds.length === 0) {
          setFavoritesCount(0);
          setFavoriteProperties([]);
          return;
        }
        const { data: props, error: propError } = await supabase
          .from('properties')
          .select('*')
          .in('id', favoriteIds);
        if (propError) throw propError;
        const existingProperties = props || [];
        setFavoriteProperties(existingProperties);
        setFavoritesCount(existingProperties.length);
        if (existingProperties.length !== favoriteIds.length) {
          await supabase
            .from('users')
            .update({ favorites: existingProperties.map(p => p.id) })
            .eq('id', userId);
        }
      } catch (err) {
        setFavoritesCount(0);
        setFavoriteProperties([]);
      }
    };
    fetchFavoritesOnLogin();
  }, [userId, setFavoritesCount, setFavoriteProperties]);

  // Aktualizuj favoritesCount po każdej zmianie ulubionych (nie licz usuniętych)
  React.useEffect(() => {
    setFavoritesCount(favoriteProperties.filter(p => !p._isDeleted).length);
  }, [favoriteProperties, setFavoritesCount]);

  // Pomocnicza funkcja obsługi pendingIntent po zalogowaniu
  const handlePendingIntent = React.useCallback(() => {
    const pending = localStorage.getItem('pendingIntent');
    if (!pending) return;
    localStorage.removeItem('pendingIntent');
    if (pending === 'addListing') {
      setSelectedTab(TAB_PROPERTIES);
      setTimeout(() => setAddListingFormOpen(true), 500);
    } else if (pending === 'addProfile') {
      setSelectedTab(TAB_TENANTS);
      setTimeout(() => setAddListingFormOpen(true), 500);
    } else if (pending === 'openMessages') {
      setTimeout(() => setMessagesInboxOpen(true), 500);
    } else if (pending === 'message') {
      const propertyId = localStorage.getItem('pendingPropertyId');
      localStorage.removeItem('pendingPropertyId');
      if (propertyId) {
        supabase.from('properties').select('*').eq('id', propertyId).maybeSingle()
          .then(({ data }) => {
            if (data) setTimeout(() => handlePropertyClickWithTracking(data), 500);
          });
      }
    } else {
      setTimeout(() => setWelcomeSnackbarOpen(true), 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlePropertyClickWithTracking]);

  // Po zalogowaniu sprawdź czy był zapisany pendingIntent (np. po potwierdzeniu emaila)
  React.useEffect(() => {
    if (!userId) return;
    handlePendingIntent();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wczytaj ogłoszenia i profile najemcy zalogowanego użytkownika (przycisk "Mój profil", tekst formularza), wyczyść po wylogowaniu
  React.useEffect(() => {
    if (userId) {
      fetchUserListings(userId);
      fetchUserTenantProfiles(userId);
    } else {
      setUserListings([]);
      setUserTenantProfiles([]);
    }
  }, [userId, fetchUserListings, fetchUserTenantProfiles, setUserListings, setUserTenantProfiles]);

  return (
    <Box sx={{ 
      flexGrow: 1,
      minHeight: '100vh',
      background: `
        linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%),
        url(${backgroundImage}) center center / cover no-repeat
      `,
      backgroundAttachment: 'fixed'
    }}>
      {/* Header */}
      <AppBar 
        position="sticky" 
        sx={{ 
          backgroundColor: 'white', 
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0',
          top: 0,
          zIndex: 1100
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', py: 0, px: '0 !important', minHeight: '72px' }}>
            {/* Logo */}
            <Box 
              onClick={() => { setSelectedTab(TAB_TENANTS); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              sx={{ display: 'flex', alignItems: 'center', height: '72px', cursor: 'pointer' }}
            >
              <img 
                src={logoImage} 
                alt="dobrynajemca.pl" 
                style={{ 
                  height: '43px',
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Desktop Navigation - Commented out for now */}
            {/* 
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {menuItems.map((item) => (
                  <Button
                    key={item}
                    sx={{
                      color: item === 'Home' ? '#D2691E' : '#666',
                      textTransform: 'none',
                      fontSize: '16px',
                      fontWeight: item === 'Home' ? 'bold' : 'normal'
                    }}
                  >
                    {item}
                  </Button>
                ))}
              </Box>
            )}
            */}

            {/* Auth Buttons & Mobile Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isMobile && (
                <>
                  {/* Ikona wiadomości — widoczna tylko po zalogowaniu */}
                  {isUserLoggedIn && (
                    <IconButton 
                      disableRipple
                      disableFocusRipple
                      sx={{
                        color: '#666',
                        width: 40,
                        height: 40,
                        outline: 'none',
                        border: 'none',
                        boxShadow: 'none',
                        backgroundColor: 'transparent',
                        padding: 0,
                        margin: '0 8px',
                        '&:hover, &:focus': {
                          color: '#D2691E',
                          backgroundColor: 'rgba(210, 105, 30, 0.1)',
                          outline: 'none',
                          border: 'none',
                          boxShadow: 'none',
                        }
                      }}
                      onClick={() => setMessagesInboxOpen(true)}
                    >
                      <Badge badgeContent={unreadMessages.unreadCount} color="error" invisible={unreadMessages.unreadCount === 0}>
                        <EmailIcon />
                      </Badge>
                    </IconButton>
                  )}

                  <IconButton 
                    disableRipple
                    disableFocusRipple
                    sx={{
                      color: '#666',
                      width: 40,
                      height: 40,
                      outline: 'none',
                      border: 'none',
                      boxShadow: 'none',
                      backgroundColor: 'transparent',
                      padding: 0,
                      margin: '0 8px',
                      '&:hover, &:focus': {
                        color: '#D2691E',
                        backgroundColor: 'rgba(210, 105, 30, 0.1)',
                        outline: 'none',
                        border: 'none',
                        boxShadow: 'none',
                      }
                    }}
                    onClick={handleFavoritesClick}
                  >
                    <Badge badgeContent={favoritesCount + tenantFavorites.favoriteTenantsCount} color="error" invisible={favoritesCount + tenantFavorites.favoriteTenantsCount === 0}>
                      <FavoriteIcon />
                    </Badge>
                  </IconButton>
                  

                  <IconButton 
                    disableRipple
                    disableFocusRipple
                    sx={{ 
                      color: '#666',
                      width: 40,
                      height: 40,
                      outline: 'none',
                      border: 'none',
                      boxShadow: 'none',
                      backgroundColor: 'transparent',
                      padding: 0,
                      margin: '0 8px',
                      '&:hover, &:focus': {
                        color: '#D2691E',
                        backgroundColor: 'rgba(210, 105, 30, 0.1)',
                        outline: 'none',
                        border: 'none',
                        boxShadow: 'none',
                      }
                    }}
                    onClick={(e) => {
                      if (isUserLoggedIn) {
                        setUserMenuAnchor(userMenuAnchor ? null : e.currentTarget);
                      } else {
                        setLoginIntent('profile');
                        setAuthModalOpen(true);
                      }
                    }}
                  >
                    <Badge
                      variant="dot"
                      color="success"
                      invisible={!isUserLoggedIn}
                      sx={{
                        '& .MuiBadge-dot': {
                          backgroundColor: '#28A745',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          border: '2px solid white'
                        }
                      }}
                    >
                      <PersonIcon />
                    </Badge>
                  </IconButton>

                  {/* Menu użytkownika po zalogowaniu */}
                  {isUserLoggedIn && (
                    <Popover
                      open={Boolean(userMenuAnchor)}
                      anchorEl={userMenuAnchor}
                      onClose={() => setUserMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{ sx: { p: 2, minWidth: 200 } }}
                    >
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>{userEmail}</Typography>
                      <Button 
                        fullWidth 
                        sx={{ 
                          mb: 1, 
                          textTransform: 'none', 
                          justifyContent: 'flex-start',
                          color: '#000'
                        }}
                        onClick={async () => {
                          await fetchUserListings(userId);
                          await fetchUserTenantProfiles(userId);
                          setMyListingsOpen(true);
                          setUserMenuAnchor(null);
                        }}
                      >
                        Moje ogłoszenia
                      </Button>
                      <Button 
                        fullWidth 
                        sx={{ 
                          mb: 1, 
                          textTransform: 'none', 
                          justifyContent: 'flex-start',
                          color: '#000'
                        }}
                        onClick={() => {
                          setSettingsModalOpen(true);
                          setUserMenuAnchor(null);
                        }}
                      >
                        Ustawienia
                      </Button>
                      <Button fullWidth color="error" variant="outlined" sx={{ textTransform: 'none' }} onClick={async () => { await handleLogout(); setSelectedTab(TAB_TENANTS); }}>Wyloguj</Button>
                    </Popover>
                  )}
                  
                  <Button 
                    variant="contained"
                    startIcon={showMyProfileButton ? <PersonIcon /> : <AddIcon />}
                    onClick={() => {
                      if (showMyProfileButton) {
                        openMyTenantProfile();
                      } else if (userId) {
                        setAddListingFormOpen(true);
                        if (window.gtag) window.gtag('event', 'dodaj_ogloszenie_click', { event_category: 'engagement' });
                      } else {
                        localStorage.setItem('pendingIntent', selectedTab === TAB_TENANTS ? 'addProfile' : 'addListing');
                        setLoginIntent(selectedTab === TAB_TENANTS ? 'addProfile' : 'addListing');
                        setAuthModalOpen(true);
                      }
                    }}
                    sx={{ 
                      backgroundColor: '#FB923C',
                      '&:hover': { backgroundColor: '#EA580C' },
                      textTransform: 'none',
                      px: 3,
                      borderRadius: 2
                    }}
                  >
                    {headerButtonLabel}
                  </Button>
                </>
              )}
              
              {isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton 
                    onClick={(e) => {
                      if (isUserLoggedIn) {
                        setUserMenuAnchor(userMenuAnchor ? null : e.currentTarget);
                      } else {
                        setLoginIntent('profile');
                        setAuthModalOpen(true);
                      }
                    }}
                    sx={{ color: '#666', p: 1 }}
                  >
                    <Badge
                      variant="dot"
                      color="success"
                      invisible={!isUserLoggedIn}
                      sx={{
                        '& .MuiBadge-dot': {
                          backgroundColor: '#28A745',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          border: '1.5px solid white'
                        }
                      }}
                    >
                      <PersonIcon />
                    </Badge>
                  </IconButton>
                  <IconButton onClick={toggleMobileMenu} sx={{ color: 'black', p: 1 }}>
                    <MenuIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          '& .MuiDrawer-paper': {
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderLeft: '1px solid #e2e8f0'
          }
        }}
      >
        <Box sx={{ width: 280, pt: 3 }}>
          <Box 
            onClick={() => { setSelectedTab(TAB_TENANTS); toggleMobileMenu(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            sx={{ px: 3, pb: 2, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <img 
              src={logoImage} 
              alt="dobrynajemca.pl" 
              style={{ 
                height: '28px', 
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </Box>
          
          <List>
            {/* Menu items - commented out for future use */}
            {/* 
            {menuItems.map((item, index) => (
              <ListItem 
                button 
                key={item} 
                onClick={toggleMobileMenu}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease'
                  },
                  animation: `slideIn 0.3s ease-out ${index * 0.1}s both`
                }}
              >
                <ListItemText 
                  primary={item} 
                  sx={{
                    '& .MuiTypography-root': {
                      color: item === 'Home' ? '#D2691E' : '#475569',
                      fontWeight: item === 'Home' ? 'bold' : 'normal',
                      fontSize: '16px'
                    }
                  }}
                />
              </ListItem>
            ))}
            */}
            
            <Box sx={{ mt: 2, px: 2 }}>
              {isUserLoggedIn ? (
                // Menu dla zalogowanych użytkowników
                <>
                  <Button 
                    fullWidth 
                    startIcon={<HomeWorkIcon />}
                    onClick={async () => {
                      await fetchUserListings(userId);
                      await fetchUserTenantProfiles(userId);
                      setMyListingsOpen(true);
                      setMobileMenuOpen(false);
                    }}
                    sx={{ 
                      color: '#475569',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      py: 1.5,
                      mb: 1
                    }}
                  >
                    Moje ogłoszenia
                  </Button>
                  
                  <Button 
                    fullWidth 
                    startIcon={<FavoriteIcon />}
                    onClick={() => {
                      setFavoritesOpen(true);
                      setMobileMenuOpen(false);
                    }}
                    sx={{ 
                      color: '#475569',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      py: 1.5,
                      mb: 1
                    }}
                  >
                    Ulubione
                    {(favoritesCount + tenantFavorites.favoriteTenantsCount) > 0 && (
                      <Box sx={{ 
                        ml: 'auto', 
                        backgroundColor: '#ef4444', 
                        color: '#fff', 
                        borderRadius: '50%', 
                        minWidth: 22, 
                        height: 22, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        lineHeight: 1
                      }}>
                        {favoritesCount + tenantFavorites.favoriteTenantsCount}
                      </Box>
                    )}
                  </Button>
                  
                  <Button 
                    fullWidth 
                    startIcon={<EmailIcon />}
                    onClick={() => {
                      setMessagesInboxOpen(true);
                      setMobileMenuOpen(false);
                    }}
                    sx={{ 
                      color: '#475569',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      py: 1.5,
                      mb: 1
                    }}
                  >
                    Wiadomości
                    {unreadMessages.unreadCount > 0 && (
                      <Box sx={{ 
                        ml: 'auto', 
                        backgroundColor: '#ef4444', 
                        color: '#fff', 
                        borderRadius: '50%', 
                        minWidth: 22, 
                        height: 22, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        lineHeight: 1
                      }}>
                        {unreadMessages.unreadCount}
                      </Box>
                    )}
                  </Button>
                  
                  <Button 
                    fullWidth 
                    startIcon={<SettingsIcon />}
                    onClick={() => {
                      setSettingsModalOpen(true);
                      setMobileMenuOpen(false);
                    }}
                    sx={{ 
                      color: '#475569',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      py: 1.5,
                      mb: 1
                    }}
                  >
                    Ustawienia
                  </Button>
                  
                  <Button 
                    fullWidth
                    variant="contained"
                    startIcon={showMyProfileButton ? <PersonIcon /> : <AddIcon />}
                    onClick={() => {
                      if (showMyProfileButton) {
                        openMyTenantProfile();
                      } else {
                        setAddListingFormOpen(true);
                        if (window.gtag) window.gtag('event', 'dodaj_ogloszenie_click', { event_category: 'engagement' });
                      }
                      setMobileMenuOpen(false);
                    }}
                    sx={{ 
                      backgroundColor: '#FB923C',
                      '&:hover': { backgroundColor: '#EA580C' },
                      textTransform: 'none',
                      py: 1.5,
                      mb: 2,
                      borderRadius: 2
                    }}
                  >
                    {headerButtonLabel}
                  </Button>
                  
                  <Button 
                    fullWidth
                    variant="outlined"
                    startIcon={<ExitToAppIcon />}
                    onClick={async () => {
                      setMobileMenuOpen(false); // Zamknij menu hamburger
                      
                      try {
                        await handleLogout(); // Wyloguj użytkownika
                        setSelectedTab(TAB_TENANTS); // Wróć na stronę główną
                        
                        // Wymuszenie wyczyszczenia stanu na poziomie App.js
                        setIsUserLoggedIn(false);
                        setUserId(null);
                        setUserEmail('');
                      } catch (error) {
                      }
                    }}
                    sx={{ 
                      borderColor: '#EF4444',
                      color: '#EF4444',
                      '&:hover': { 
                        borderColor: '#DC2626',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        color: '#DC2626'
                      },
                      textTransform: 'none',
                      py: 1.5,
                      borderRadius: 2
                    }}
                  >
                    Wyloguj się
                  </Button>
                </>
              ) : (
                // Menu dla niezalogowanych użytkowników
                <>
                  <Button 
                    fullWidth 
                    startIcon={<FavoriteIcon />}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setLoginIntent('favorites');
                      setAuthModalOpen(true);
                    }}
                    sx={{ 
                      color: '#475569',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      py: 1.5,
                      mb: 1
                    }}
                  >
                    Ulubione
                  </Button>
                  
                  <Button 
                    fullWidth 
                    startIcon={<PersonIcon />}
                    onClick={() => {
                      setAuthModalOpen(true);
                      setMobileMenuOpen(false);
                    }}
                    sx={{ 
                      color: '#475569',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      py: 1.5,
                      mb: 1
                    }}
                  >
                    Zaloguj się
                  </Button>
                  
                  <Button 
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      localStorage.setItem('pendingIntent', selectedTab === TAB_TENANTS ? 'addProfile' : 'addListing');
                      setLoginIntent(selectedTab === TAB_TENANTS ? 'addProfile' : 'addListing');
                      setAuthModalOpen(true);
                      setMobileMenuOpen(false);
                    }}
                    sx={{ 
                      backgroundColor: '#FB923C',
                      '&:hover': { backgroundColor: '#EA580C' },
                      textTransform: 'none',
                      py: 1.5,
                      borderRadius: 2
                    }}
                  >
                    {addButtonLabel}
                  </Button>
                </>
              )}
            </Box>
          </List>
        </Box>
      </Drawer>

      {/* Hero Title Section */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography 
            variant="h2" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              color: 'white',
              mb: { xs: 1, md: 2 }, // Mniejszy margin na mobile
              fontSize: { xs: '29px', md: '3rem', lg: '3.5rem' },
              lineHeight: 1.2,
              textShadow: '2px 2px 8px rgba(0,0,0,0.7)'
            }}
          >
            {selectedTab === TAB_PROPERTIES 
              ? 'Znajdź wymarzone mieszkanie, bez pośredników' 
              : 'Osoby aktualnie szukające mieszkania na wynajem'}
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'white',
              mb: 4,
              fontSize: { xs: '1rem', md: '1.2rem' },
              opacity: 0.95,
              textShadow: '1px 1px 4px rgba(0,0,0,0.5)',
              lineHeight: 1.6,
              maxWidth: '800px',
              margin: '0 auto',
              display: { xs: 'none', md: 'block' } // Ukryj sub header na mobile
            }}
          >
            {selectedTab === TAB_PROPERTIES 
              ? <>Sprawdź aktualne ogłoszenia mieszkań. Ustaw filtry, aby znaleźć oferty dopasowane do&nbsp;lokalizacji, ceny i&nbsp;Twoich potrzeb.</>
              : <>Odkryj profile osób, aktualnie szukających mieszkania</>
            }
          </Typography>
        </Box>
      </Container>

      {/* Search Filter Section - Moved to top */}
      <Container maxWidth="lg" sx={{ mt: { xs: 3, md: 7 }, mb: { xs: 0, sm: 2 }, px: { xs: 0, md: 3 } }}>
        <Box sx={{ position: 'relative' }}>
          {/* Custom Tab Cards - attached to filter */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            justifyContent: 'flex-start',
            position: 'relative',
            zIndex: 1,
            mb: -1 // Overlapping with the filter
          }}>
            {['Najemcy', 'Mieszkania'].map((label, index) => (
              <Box
                key={label}
                onClick={() => {
                  if (index === selectedTab) return; // brak zmiany

                  // Zapisz filtry bieżącego taba
                  if (selectedTab === TAB_PROPERTIES) {
                    setPropertyFiltersSnapshot({ ...searchFilters });
                  } else {
                    setTenantFiltersSnapshot({ ...searchFilters });
                  }

                  // Przywróć filtry docelowego taba
                  const targetSnapshot = index === TAB_PROPERTIES ? propertyFiltersSnapshot : tenantFiltersSnapshot;
                  setSearchFilters(targetSnapshot);

                  // Synchronizuj inputy autocomplete z przywróconym stanem
                  setCityInputValue(targetSnapshot.city || '');
                  setDistrictInputValue(targetSnapshot.district || '');
                  setStreetInputValue(targetSnapshot.street || '');

                  setSelectedTab(index);
                  setSearchResults(null);
                  if (window.gtag) {
                    window.gtag('event', 'page_view', {
                      page_path: index === TAB_TENANTS ? '/' : '/mieszkania',
                      page_title: index === TAB_TENANTS ? 'Najemcy — dobrynajemca.pl' : 'Mieszkania — dobrynajemca.pl'
                    });
                  }
                }}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: '12px 12px 0 0', // Rounded only at top
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  minWidth: '150px',
                  backgroundColor: index === TAB_TENANTS ? '#ffd7b5' : 'white',
                  color: index === TAB_TENANTS ? '#D2691E' : (selectedTab === index ? '#D2691E' : '#666'),
                  border: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: index === TAB_TENANTS ? '#ffcca3' : 'white',
                    color: '#D2691E'
                  }
                }}
              >
                {label}
              </Box>
            ))}
          </Box>

          {/* Search Filters */}
          <Paper 
            elevation={3}
            sx={{ 
              p: 3,
              pt: 4, // Extra padding at top for tabs
              borderRadius: { xs: 0, md: 3 },
              backgroundColor: selectedTab === TAB_TENANTS ? '#ffd7b5' : 'white',
              position: 'relative',
              zIndex: 2,
              transition: 'background-color 0.2s ease',
              mx: { xs: 0, md: 0 }
            }}
          >
          <Grid container spacing={2} alignItems={isMobile ? "flex-start" : "center"} direction={isMobile ? "column" : "row"} sx={{ px: isMobile ? 2 : 0 }}>
            {/* Miasto */}
            <Grid item xs={12} md={2.5} sx={ isMobile ? { display: 'flex', justifyContent: 'center', width: '100%' } : {} }>
              <Box sx={{ width: '100%' }}>
                <Autocomplete
                  freeSolo
                  options={filteredCityOptions}
                  filterOptions={(x) => x}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option?.displayName || option?.name || ''}
                  isOptionEqualToValue={(option, value) => (option?.name || option) === (value?.name || value)}
                  value={searchFilters.city || ''}
                  inputValue={cityInputValue}
                  onInputChange={handleCityInputChange}
                  onChange={wrappedHandleCityChange}
                  loading={isLoadingCities}
                  loadingText="Ładowanie miast..."
                  noOptionsText="Brak wyników"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Miasto"
                      placeholder="Wybierz"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationIcon sx={{ color: '#EE2005' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {isLoadingCities ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        backgroundColor: '#F9FAFB',
                        cursor: 'text',
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          width: isMobile ? '100%' : '180px',
                          minWidth: isMobile ? '100%' : '180px',
                          maxWidth: isMobile ? '100%' : '180px',
                          fontSize: '14px'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '16px', color: '#374151', letterSpacing: '0.05em'
                        },
                        '& .MuiInputLabel-root.Mui-shrink': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' },
                        '& .MuiInputBase-input': { color: '#111111' }
                      }}
                    />
                  )}
                  sx={{ width: '100%' }}
                />
              </Box>
            </Grid>

            {/* Dzielnica */}
              <Grid item xs={12} md={2.5} sx={ isMobile ? { display: 'flex', justifyContent: 'center', width: '100%' } : {} }>
                <Box sx={{ width: '100%' }}>
                  <Autocomplete
                    freeSolo
                    loading={isLoadingDistricts}
                    loadingText="Ładowanie dzielnic..."
                    filterOptions={(x) => x}
                    options={[{ name: 'Dowolna', displayName: 'Dowolna', isAll: true }, ...districtSuggestions]}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option?.displayName || option?.name || ''}
                    isOptionEqualToValue={(option, value) => (option?.name || option) === (value?.name || value)}
                    value={searchFilters.district || ''}
                    inputValue={districtInputValue}
                    onInputChange={(event, newInputValue) => handleDistrictInputChange(event, newInputValue)}
                    onChange={(event, newValue) => {
                      if (newValue?.isAll) {
                        wrappedHandleDistrictChange(event, '');
                        setDistrictInputValue('');
                      } else {
                        wrappedHandleDistrictChange(event, newValue);
                      }
                    }}
                    onOpen={() => {
                      if (searchFilters.city) {
                        import('./services/streetApi').then(api => {
                          api.fetchDistricts(searchFilters.city, '').then(districts => {
                            setDistrictSuggestions(districts || []);
                          });
                        });
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option?.name || option} style={option?.isAll ? { fontWeight: 'bold', borderBottom: '1px solid #eee' } : {}}>
                        {option?.displayName || option?.name || option}
                      </li>
                    )}
                    noOptionsText="Brak dzielnic dla tego miasta"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<>Dzielnica <span style={{ color: '#D2691E', fontWeight: 'normal', fontSize: '12px' }}>(opcjonalnie)</span></>}
                        placeholder="Dowolna"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon sx={{ color: '#D2691E' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          backgroundColor: '#F9FAFB',
                          cursor: 'text',
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            width: isMobile ? '100%' : '180px',
                            minWidth: isMobile ? '100%' : '180px',
                            maxWidth: isMobile ? '100%' : '180px',
                            fontSize: '14px'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '16px', color: '#374151', letterSpacing: '0.05em'
                          },
                          '& .MuiInputLabel-root.Mui-shrink': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' },
                          '& .MuiInputBase-input': { color: '#111111' }
                        }}
                      />
                    )}
                    sx={{ width: '100%' }}
                  />
                </Box>
              </Grid>
            
            {/* Cena */}
            <Grid item xs={12} md={2.5} sx={ isMobile ? { display: 'flex', justifyContent: 'center', width: '100%' } : {} }>
              <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Cena"
                value={searchFilters.priceRange.min || searchFilters.priceRange.max 
                  ? `${searchFilters.priceRange.min || '0'} - ${searchFilters.priceRange.max || '∞'} zł`
                  : priceCleared ? 'Dowolna' : 'Wybierz'}
                onClick={handlePopoverOpen('price')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon sx={{ color: '#D2691E' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <ExpandMoreIcon />
                    </InputAdornment>
                  ),
                }}
                className="filter-input"
                sx={{
                  backgroundColor: '#F9FAFB',
                  cursor: 'pointer',
                  '& .MuiOutlinedInput-root': {
                    width: isMobile ? '100%' : '320px',
                    minWidth: isMobile ? '100%' : '320px',
                    fontSize: '14px'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '16px', color: '#374151', letterSpacing: '0.05em'
                  },
                  '& .MuiInputLabel-root.Mui-shrink': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' },
                  '& .MuiInputBase-input': {
                    textOverflow: 'clip',
                    overflow: 'visible',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    color: '#111111'
                  }
                }}
                readOnly
              />
              <Popover
                open={Boolean(anchorEls.price)}
                anchorEl={anchorEls.price}
                onClose={() => { if (!searchFilters.priceRange.min && !searchFilters.priceRange.max) setPriceCleared(true); handlePopoverClose('price')(); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'center' : 'left' }}
                disableScrollLock={!isMobile}
                slotProps={isMobile ? { paper: { sx: { left: '50% !important', transform: 'translateX(-50%) !important', maxWidth: '95vw', overflow: 'visible' } } } : {}}
              >
                <Box sx={{ p: 3, width: { xs: '85vw', sm: 420 }, maxWidth: '95vw' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Zakres ceny (zł/mies.):
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleFilterChange('priceRange', { min: '', max: '' });
                        setPriceCleared(true);
                        handlePopoverClose('price')();
                      }}
                      sx={{ color: '#999', '&:hover': { color: '#333' } }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ px: 1 }}>
                    {/* Popularne kwoty */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 500, mb: 1, display: 'block' }}>
                        Popularne:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[2500, 3000, 3500, 4000, 6000].map(val => (
                          <Chip
                            key={val}
                            label={`do ${val.toLocaleString('pl-PL')}`}
                            size="small"
                            onClick={() => {
                              handleFilterChange('priceRange', { min: searchFilters.priceRange.min || '', max: String(val) });
                              setPriceCleared(false);
                            }}
                            variant={parseInt(searchFilters.priceRange.max) === val ? 'filled' : 'outlined'}
                            sx={{
                              cursor: 'pointer',
                              borderColor: '#D2691E',
                              backgroundColor: parseInt(searchFilters.priceRange.max) === val ? '#D2691E' : 'transparent',
                              '& .MuiChip-label': { color: parseInt(searchFilters.priceRange.max) === val ? '#fff' : '#D2691E' },
                              '&:hover': { backgroundColor: '#D2691E !important', borderColor: '#D2691E' },
                              '&:hover .MuiChip-label': { color: '#fff !important' },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <Slider
                      value={[
                        parseInt(searchFilters.priceRange.min) || 1500,
                        parseInt(searchFilters.priceRange.max) || 4500
                      ]}
                      onChange={(e, newValue) => {
                        handleFilterChange('priceRange', {
                          min: String(newValue[0]),
                          max: String(newValue[1])
                        });
                        setPriceCleared(false);
                      }}
                      min={500}
                      max={10000}
                      step={100}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => v >= 10000 ? '10 000+ zł' : `${v.toLocaleString('pl-PL')} zł`}
                      sx={{
                        color: '#D2691E',
                        '& .MuiSlider-thumb': { backgroundColor: '#D2691E' },
                        '& .MuiSlider-track': { backgroundColor: '#D2691E' },
                        '& .MuiSlider-rail': { backgroundColor: '#e0d0c0' },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="body2" sx={{ color: '#555' }}>
                        {(parseInt(searchFilters.priceRange.min) || 1500).toLocaleString('pl-PL')} zł
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#555' }}>
                        {(parseInt(searchFilters.priceRange.max) || 4500) >= 10000 ? '10 000+ zł' : `${(parseInt(searchFilters.priceRange.max) || 4500).toLocaleString('pl-PL')} zł`}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => { if (!searchFilters.priceRange.min && !searchFilters.priceRange.max) setPriceCleared(true); handlePopoverClose('price')(); }}
                    sx={{ mt: 2, backgroundColor: '#D2691E', '&:hover': { backgroundColor: '#A0522D' } }}
                  >
                    Zatwierdź
                  </Button>
                </Box>
              </Popover>
              </Box>
            </Grid>

            {/* Przycisk Więcej filtrów na mobile */}
            {isMobile && !moreFiltersOpen && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: '250px' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setMoreFiltersOpen(true)}
                  startIcon={<ExpandMoreIcon />}
                  sx={{
                    mt: 1,
                    borderColor: '#D2691E',
                    color: '#D2691E',
                    fontSize: '14px',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#A0522D',
                      backgroundColor: 'rgba(210, 105, 30, 0.04)'
                    }
                  }}
                >
                  Więcej filtrów
                </Button>
                </Box>
              </Grid>
            )}

            {/* Ilość pokoi - ukryte pod więcej filtrów na mobile */}
            {(!isMobile || moreFiltersOpen) && (
            <Grid item xs={12} md={2.5} sx={ isMobile ? { display: 'flex', justifyContent: 'center', width: '100%' } : {} }>
              <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Ilość pokoi"
                value={searchFilters.rooms.length > 0 ? 
                  (searchFilters.rooms.length === 1 ? 
                    (searchFilters.rooms[0] === 0 ? 'Pokój w mieszkaniu' :
                     searchFilters.rooms[0] === 1 ? 'Kawalerka' : 
                     searchFilters.rooms[0] === 4 ? '4+ pokoje' : 
                     `${searchFilters.rooms[0]} pokoje`) : 
                    `${searchFilters.rooms.length} wybranych`) 
                  : roomsTouched ? 'Dowolna' : 'Wybierz'}
                onClick={handlePopoverOpen('rooms')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeIcon sx={{ color: '#D2691E' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <ExpandMoreIcon />
                    </InputAdornment>
                  ),
                }}
                className="filter-input"
                sx={{ backgroundColor: '#F9FAFB', cursor: 'pointer', '& .MuiOutlinedInput-root': { fontSize: '14px' }, '& .MuiInputLabel-root': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' }, '& .MuiInputLabel-root.Mui-shrink': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' } }}
                readOnly
              />
              <Popover
                open={Boolean(anchorEls.rooms)}
                anchorEl={anchorEls.rooms}
                onClose={() => { if (searchFilters.rooms.length === 0) setRoomsTouched(true); handlePopoverClose('rooms')(); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'center' : 'left' }}
                disableScrollLock={true}
                slotProps={isMobile ? { paper: { sx: { left: '50% !important', transform: 'translateX(-50%) !important', maxWidth: '95vw' } } } : {}}
              >
                <Box sx={{ p: 2, width: { xs: '85vw', sm: 350 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Wybierz ilość pokoi:
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        updateSearchFilter('rooms', []);
                        setRoomsTouched(true);
                        handlePopoverClose('rooms')();
                      }}
                      sx={{ color: '#999', '&:hover': { color: '#333' } }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {[1, 2, 3, 4].map((roomCount) => (
                    <FormControlLabel
                      key={roomCount}
                      control={
                        <Checkbox
                          checked={searchFilters.rooms.includes(roomCount)}
                          onChange={() => {
                            handleRoomToggle(roomCount);
                            handlePopoverClose('rooms')();
                          }}
                          sx={{ '&.Mui-checked': { color: '#D2691E' } }}
                        />
                      }
                      label={roomCount === 1 ? 'Kawalerka' : roomCount === 4 ? '4+ pokoje' : `${roomCount} pokoje`}
                      sx={{ display: 'block', mb: 1 }}
                    />
                  ))}
                  <FormControlLabel
                    key={0}
                    control={
                      <Checkbox
                        checked={searchFilters.rooms.includes(0)}
                        onChange={() => {
                          handleRoomToggle(0);
                          handlePopoverClose('rooms')();
                        }}
                        sx={{ '&.Mui-checked': { color: '#D2691E' } }}
                      />
                    }
                    label="Pokój w mieszkaniu"
                    sx={{ display: 'block', mb: 1 }}
                  />
                </Box>
              </Popover>
              </Box>
            </Grid>
            )}
            {(!isMobile || moreFiltersOpen) && (
              <Grid item xs={12} md={2.5} sx={ isMobile ? { display: 'flex', justifyContent: 'center', width: '100%' } : {} }>
                <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Metraż"
                  value={searchFilters.sizeRange.min || searchFilters.sizeRange.max 
                    ? `${searchFilters.sizeRange.min || '0'} - ${searchFilters.sizeRange.max || '∞'} m²`
                    : sizeCleared ? 'Dowolny' : 'Wybierz'}
                  onClick={handlePopoverOpen('size')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SquareFootIcon sx={{ color: '#D2691E' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <ExpandMoreIcon />
                      </InputAdornment>
                    ),
                  }}
                  className="filter-input"
                  sx={{
                    backgroundColor: '#F9FAFB',
                    cursor: 'pointer',
                    '& .MuiOutlinedInput-root': {
                      width: isMobile ? '100%' : '320px',
                      minWidth: isMobile ? '100%' : '320px',
                      fontSize: '14px'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '16px', color: '#374151', letterSpacing: '0.05em'
                    },
                    '& .MuiInputLabel-root.Mui-shrink': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' },
                    '& .MuiInputBase-input': {
                      textOverflow: 'clip',
                      overflow: 'visible',
                      whiteSpace: 'nowrap',
                      fontSize: '14px',
                      color: '#111111'
                    }
                  }}
                  readOnly
                />
                <Popover
                  open={Boolean(anchorEls.size)}
                  anchorEl={anchorEls.size}
                  onClose={() => { if (!searchFilters.sizeRange.min && !searchFilters.sizeRange.max) setSizeCleared(true); handlePopoverClose('size')(); }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'center' : 'left' }}
                  disableScrollLock={!isMobile}
                  slotProps={isMobile ? { paper: { sx: { left: '50% !important', transform: 'translateX(-50%) !important', maxWidth: '95vw', overflow: 'visible' } } } : {}}
                >
                  <Box sx={{ p: 3, width: { xs: '85vw', sm: 420 }, maxWidth: '95vw' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Metraż (m²):
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleFilterChange('sizeRange', { min: '', max: '' });
                          setSizeCleared(true);
                          handlePopoverClose('size')();
                        }}
                        sx={{ color: '#999', '&:hover': { color: '#333' } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ px: 1 }}>
                      {/* Popularne metraże */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 500, mb: 1, display: 'block' }}>
                          Popularne:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {[35, 40, 50, 60].map(val => (
                            <Chip
                              key={val}
                              label={`do ${val} m²`}
                              size="small"
                              onClick={() => {
                                handleFilterChange('sizeRange', { min: searchFilters.sizeRange.min || '', max: String(val) });
                                setSizeCleared(false);
                              }}
                              variant={parseInt(searchFilters.sizeRange.max) === val ? 'filled' : 'outlined'}
                              sx={{
                                cursor: 'pointer',
                                borderColor: '#D2691E',
                                backgroundColor: parseInt(searchFilters.sizeRange.max) === val ? '#D2691E' : 'transparent',
                                '& .MuiChip-label': { color: parseInt(searchFilters.sizeRange.max) === val ? '#fff' : '#D2691E' },
                                '&:hover': { backgroundColor: '#D2691E !important', borderColor: '#D2691E' },
                                '&:hover .MuiChip-label': { color: '#fff !important' },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                      <Slider
                        value={[
                          parseInt(searchFilters.sizeRange.min) || 25,
                          parseInt(searchFilters.sizeRange.max) || 50
                        ]}
                        onChange={(e, newValue) => {
                          handleFilterChange('sizeRange', {
                            min: String(newValue[0]),
                            max: String(newValue[1])
                          });
                          setSizeCleared(false);
                        }}
                        min={10}
                        max={100}
                        step={5}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => v >= 100 ? '100+ m²' : `${v} m²`}
                        sx={{
                          color: '#D2691E',
                          '& .MuiSlider-thumb': { backgroundColor: '#D2691E' },
                          '& .MuiSlider-track': { backgroundColor: '#D2691E' },
                          '& .MuiSlider-rail': { backgroundColor: '#e0d0c0' },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2" sx={{ color: '#555' }}>
                          {parseInt(searchFilters.sizeRange.min) || 25} m²
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#555' }}>
                          {(parseInt(searchFilters.sizeRange.max) || 50) >= 100 ? '100+ m²' : `${parseInt(searchFilters.sizeRange.max) || 50} m²`}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => { if (!searchFilters.sizeRange.min && !searchFilters.sizeRange.max) setSizeCleared(true); handlePopoverClose('size')(); }}
                      sx={{ mt: 2, backgroundColor: '#D2691E', '&:hover': { backgroundColor: '#A0522D' } }}
                    >
                      Zatwierdź
                    </Button>
                  </Box>
                </Popover>
                </Box>
              </Grid>
            )}

            {/* Kiedy dodane tylko dla "Szukam mieszkania" */}
            {selectedTab === TAB_PROPERTIES && (!isMobile || moreFiltersOpen) && (
              <Grid item xs={12} md={2.5} sx={ isMobile ? { display: 'flex', justifyContent: 'center', width: '100%' } : {} }>
                <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Kiedy dodane"
                  value={searchFilters.dateAdded ? {
                    'today': 'Dzisiaj',
                    '3days': '3 dni',
                    '7days': '7 dni',
                    '30days': '30 dni'
                  }[searchFilters.dateAdded] : 'Dowolne'}
                  onClick={handlePopoverOpen('dateAdded')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ScheduleIcon sx={{ color: '#D2691E' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <ExpandMoreIcon />
                      </InputAdornment>
                    ),
                  }}
                  className="filter-input"
                  sx={{ backgroundColor: '#F9FAFB', cursor: 'pointer', '& .MuiOutlinedInput-root': { fontSize: '14px' }, '& .MuiInputLabel-root': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' }, '& .MuiInputLabel-root.Mui-shrink': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' } }}
                  readOnly
                />
                <Popover
                  open={Boolean(anchorEls.dateAdded)}
                  anchorEl={anchorEls.dateAdded}
                  onClose={() => { if (!searchFilters.dateAdded) setDateTouched(true); handlePopoverClose('dateAdded')(); }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  disableScrollLock={true}
                >
                  <Box sx={{ p: 2, width: 300 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Kiedy dodane:
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleFilterChange('dateAdded', '');
                          setDateTouched(true);
                          handlePopoverClose('dateAdded')();
                        }}
                        sx={{ color: '#999', '&:hover': { color: '#333' } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {[
                      { value: 'today', label: 'Dzisiaj' },
                      { value: '3days', label: '3 dni' },
                      { value: '7days', label: '7 dni' },
                      { value: '30days', label: '30 dni' }
                    ].map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={searchFilters.dateAdded === option.value}
                            onChange={() => {
                              handleFilterChange('dateAdded', option.value);
                              handlePopoverClose('dateAdded')();
                            }}
                            sx={{ '&.Mui-checked': { color: '#D2691E' } }}
                          />
                        }
                        label={option.label}
                        sx={{ display: 'block', mb: 1 }}
                      />
                    ))}
                  </Box>
                </Popover>
                </Box>
              </Grid>
            )}

            {/* Udogodnienia tylko dla "Szukam mieszkania" */}
            {selectedTab === TAB_PROPERTIES && (!isMobile || moreFiltersOpen) && (
              <Grid item xs={12} md={2.5} sx={ isMobile ? { display: 'flex', justifyContent: 'center', width: '100%' } : {} }>
                <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label={<>Udogodnienia <span style={{ color: '#D2691E', fontWeight: 'normal', fontSize: '12px' }}>(opcjonalnie)</span></>}
                  value={searchFilters.amenities.length > 0 ? 
                    (searchFilters.amenities.length === 1 ? 
                      searchFilters.amenities[0] : 
                      `${searchFilters.amenities.length} wybranych`) : 
                    amenitiesTouched ? 'Dowolne' : 'Wybierz'}
                  onClick={handlePopoverOpen('amenities')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AmenitiesIcon sx={{ color: '#D2691E' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <ExpandMoreIcon />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  className="filter-input"
                  sx={{ backgroundColor: '#F9FAFB', cursor: 'pointer', '& .MuiOutlinedInput-root': { fontSize: '14px' }, '& .MuiInputLabel-root': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' }, '& .MuiInputLabel-root.Mui-shrink': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' } }}
                  readOnly
                />
                <Popover
                  open={Boolean(anchorEls.amenities)}
                  anchorEl={anchorEls.amenities}
                  onClose={() => { if (searchFilters.amenities.length === 0) setAmenitiesTouched(true); handlePopoverClose('amenities')(); }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  disableScrollLock={true}
                >
                  <Box sx={{ p: 3, width: 380 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Wybierz mieszkania zawierające:
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          updateSearchFilter('amenities', []);
                          setAmenitiesTouched(true);
                          handlePopoverClose('amenities')();
                        }}
                        sx={{ color: '#999', '&:hover': { color: '#333' } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {availableAmenities.map((amenity) => (
                      <FormControlLabel
                        key={amenity}
                        control={
                          <Checkbox
                            checked={searchFilters.amenities.includes(amenity)}
                            onChange={() => handleAmenityToggle(amenity)}
                            sx={{
                              color: '#D2691E',
                              '&.Mui-checked': {
                                color: '#D2691E',
                              },
                            }}
                          />
                        }
                        label={amenity}
                        sx={{ 
                          display: 'block',
                          mb: 1,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.95rem'
                          }
                        }}
                      />
                    ))}
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => { if (searchFilters.amenities.length === 0) setAmenitiesTouched(true); handlePopoverClose('amenities')(); }}
                      sx={{ 
                        backgroundColor: '#D2691E', 
                        '&:hover': { backgroundColor: '#A0522D' },
                        mt: 2
                      }}
                    >
                      Zatwierdź
                    </Button>
                  </Box>
                </Popover>
                </Box>
              </Grid>
            )}

            {/* Filtry tylko dla "Szukam najemcy" */}
            {selectedTab === TAB_TENANTS && (!isMobile || moreFiltersOpen) && (
              <>
                {/* Kiedy dodane */}
                <Grid item xs={12} md={2.5} sx={ isMobile ? { display: 'flex', justifyContent: 'center', width: '100%' } : {} }>
                  <Box sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    label="Kiedy dodane"
                    value={searchFilters.dateAdded ? {
                      'today': 'Dzisiaj',
                      '3days': '3 dni',
                      '7days': '7 dni',
                      '30days': '30 dni'
                    }[searchFilters.dateAdded] : 'Dowolne'}
                    onClick={handlePopoverOpen('dateAdded')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ScheduleIcon sx={{ color: '#D2691E' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <ExpandMoreIcon />
                        </InputAdornment>
                      ),
                    }}
                    className="filter-input"
                    sx={{ backgroundColor: '#F9FAFB', cursor: 'pointer', '& .MuiOutlinedInput-root': { fontSize: '14px' }, '& .MuiInputLabel-root': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' }, '& .MuiInputLabel-root.Mui-shrink': { fontSize: '16px', color: '#374151', letterSpacing: '0.05em' } }}
                    readOnly
                  />
                  <Popover
                    open={Boolean(anchorEls.dateAdded)}
                    anchorEl={anchorEls.dateAdded}
                    onClose={() => { if (!searchFilters.dateAdded) setDateTouched(true); handlePopoverClose('dateAdded')(); }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    disableScrollLock={true}
                  >
                    <Box sx={{ p: 2, width: 300 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          Kiedy dodane:
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            handleFilterChange('dateAdded', '');
                            setDateTouched(true);
                            handlePopoverClose('dateAdded')();
                          }}
                          sx={{ color: '#999', '&:hover': { color: '#333' } }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {[
                        { value: 'today', label: 'Dzisiaj' },
                        { value: '3days', label: '3 dni' },
                        { value: '7days', label: '7 dni' },
                        { value: '30days', label: '30 dni' }
                      ].map((option) => (
                        <FormControlLabel
                          key={option.value}
                          control={
                            <Checkbox
                              checked={searchFilters.dateAdded === option.value}
                              onChange={() => {
                                handleFilterChange('dateAdded', option.value);
                                handlePopoverClose('dateAdded')();
                              }}
                              sx={{ '&.Mui-checked': { color: '#D2691E' } }}
                            />
                          }
                          label={option.label}
                          sx={{ display: 'block', mb: 1 }}
                        />
                      ))}
                    </Box>
                  </Popover>
                  </Box>
                </Grid>

                {/* Tylko niepalący */}
                <Grid item xs={12} md={2} sx={ isMobile ? { width: '100%' } : {} }>
                  <Box sx={{ width: '100%', pl: isMobile ? 2 : 0 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={searchFilters.nonSmoking}
                        onChange={(e) => handleFilterChange('nonSmoking', e.target.checked)}
                        sx={{
                          color: '#D2691E',
                          '&.Mui-checked': {
                            color: '#D2691E',
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                        <SmokingIcon sx={{ color: '#666', fontSize: '18px' }} />
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Tylko niepalący</Typography>
                      </Box>
                    }
                    sx={{ 
                      margin: 0,
                      width: '100%',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.9rem',
                        width: '100%'
                      }
                    }}
                  />
                  </Box>
                </Grid>

                {/* Tylko bez zwierząt */}
                <Grid item xs={12} md={2} sx={ isMobile ? { width: '100%' } : {} }>
                  <Box sx={{ width: '100%', pl: isMobile ? 2 : 0 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={searchFilters.noPets}
                        onChange={(e) => handleFilterChange('noPets', e.target.checked)}
                        sx={{
                          color: '#D2691E',
                          '&.Mui-checked': {
                            color: '#D2691E',
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                        <PetsIcon sx={{ color: '#666', fontSize: '18px' }} />
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Tylko bez zwierząt</Typography>
                      </Box>
                    }
                    sx={{ 
                      margin: 0,
                      width: '100%',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.9rem',
                        width: '100%'
                      }
                    }}
                  />
                  </Box>
                </Grid>

                {/* Przycisk Ukryj filtry na mobile - tylko dla najemcy */}
                {isMobile && (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ width: '200px' }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setMoreFiltersOpen(false)}
                      startIcon={<ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />}
                      sx={{
                        mt: 1,
                        borderColor: '#D2691E',
                        color: '#D2691E',
                        fontSize: '14px',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          borderColor: '#A0522D',
                          backgroundColor: 'rgba(210, 105, 30, 0.04)'
                        }
                      }}
                    >
                      Ukryj filtry
                    </Button>
                    </Box>
                  </Grid>
                )}

              </>
            )}

            {/* Przycisk Ukryj filtry na mobile - dla mieszkania */}
            {isMobile && moreFiltersOpen && selectedTab === TAB_PROPERTIES && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: '200px' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setMoreFiltersOpen(false)}
                  startIcon={<ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />}
                  sx={{
                    mt: 1,
                    borderColor: '#D2691E',
                    color: '#D2691E',
                    fontSize: '14px',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#A0522D',
                      backgroundColor: 'rgba(210, 105, 30, 0.04)'
                    }
                  }}
                >
                  Ukryj filtry
                </Button>
                </Box>
              </Grid>
            )}
            
            {/* Przycisk Szukaj */}
            <Grid item xs={12} md={2} sx={ isMobile ? { display: 'flex', justifyContent: 'center', width: '100%' } : {} }>
              <Box sx={{ width: '100%' }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  sx={{
                    backgroundColor: '#FB923C',
                    boxShadow: 'none',
                    '&:hover': { 
                      backgroundColor: '#EA580C',
                      boxShadow: 'none',
                    },
                    height: isMobile ? '48px' : '40px',
                    width: isMobile ? undefined : 'calc(100% + 20px)',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    borderRadius: 2,
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  Szukaj
                </Button>
              </Box>
            </Grid>
            </Grid>
          </Paper>
        </Box>
      </Container>

      {/* Property Carousels Section */}
      <Container maxWidth="lg" sx={{ pt: { xs: 0, sm: 2 }, pb: 4 }}>
        {dataLoading ? (
          <Box sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: { xs: 0, sm: 4 },
            p: { xs: 2, sm: 4 },
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            mx: { xs: -2, sm: 0 },
            width: { xs: 'calc(100% + 32px)', sm: 'auto' },
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 }
            },
            animation: 'fadeIn 0.3s ease'
          }}>
            {[1, 2, 3, 4].map(i => <PropertyCardSkeleton key={i} />)}
          </Box>
        ) : (searchResults !== null) ? (
          selectedTab === TAB_TENANTS ? (
            // Wyniki najemców – ten sam kontener co mieszkania
            <Box sx={{
              backgroundColor: 'white',
              borderRadius: { xs: 0, sm: 4 },
              p: { xs: 2, sm: 4 },
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              minHeight: 400,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              width: { xs: 'calc(100% + 32px)', md: '70%' },
              mx: { xs: -2, md: 'auto' },
              '@keyframes fadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 }
              },
              animation: 'fadeIn 0.3s ease'
            }}>
              <TenantProfileCarousel
                title={`Wyniki wyszukiwania (${searchResults.length})`}
                profiles={searchResults}
                onTenantClick={handleTenantClickWithUrl}
                userId={userId}
                onFavoritesChange={() => tenantFavorites.fetchFavoriteTenants()}
                favoriteIds={tenantFavorites.favoriteTenants.map(t => t.id)}
                onLoginRequired={() => { setLoginIntent('favorites'); setAuthModalOpen(true); }}
                listMode={true}
              />
            </Box>
          ) : (
            // Wyniki mieszkań
            <Box sx={{
              backgroundColor: 'white',
              borderRadius: { xs: 0, sm: 4 },
              p: { xs: 2, sm: 4 },
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              minHeight: 400,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              width: { xs: 'calc(100% + 32px)', md: '70%' },
              mx: { xs: -2, md: 'auto' },
              '@keyframes fadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 }
              },
              animation: 'fadeIn 0.3s ease'
            }}>
              <Listing properties={searchResults} onPropertyClick={handlePropertyClickWithTracking} />
            </Box>
          )
        ) : (
          <Box sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: { xs: 0, sm: 4 },
            p: { xs: 2, sm: 4 },
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            mx: { xs: -2, sm: 0 },
            width: { xs: 'calc(100% + 32px)', sm: 'auto' }
          }}>
            {/* Content based on selected tab */}
            {selectedTab === TAB_PROPERTIES ? (
              (() => {
                const imageProperties = recentProperties.filter(p => {
                  if (Array.isArray(p.image_url)) {
                    return p.image_url.length > 0 && p.image_url.some(url => url && url.trim() !== '');
                  }
                  return typeof p.image_url === 'string' && p.image_url.trim() !== '';
                });
                if (imageProperties.length === 0) {
                  return (
                    <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
                      <Typography variant="h2" sx={{ fontSize: '3rem', mb: 2 }}>🏠</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                        Brak mieszkań
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#777', mb: 3 }}>
                        Bądź pierwszy! Dodaj swoje ogłoszenie i dotrzyj do tysięcy najemców.
                      </Typography>
                      <Button
                        variant="contained"
                        size="medium"
                        onClick={() => {
                          if (!isUserLoggedIn) {
                            localStorage.setItem('pendingIntent', 'addListing');
                            setLoginIntent('addListing');
                            setAuthModalOpen(true);
                          } else {
                            setAddListingFormOpen(true);
                          }
                        }}
                        sx={{
                          backgroundColor: '#D2691E',
                          borderRadius: 3,
                          px: 3,
                          py: 1,
                          fontSize: '0.85rem',
                          fontWeight: 700,
                          '&:hover': { backgroundColor: '#B85C00' }
                        }}
                      >
                        + Dodaj ogłoszenie
                      </Button>
                    </Box>
                  );
                }
                return (
                  <>
                    <PropertyCarousel 
                      title="Ostatnio dodane" 
                      properties={imageProperties}
                      onPropertyClick={handlePropertyClickWithTracking}
                      userId={userId}
                      onFavoritesChange={() => fetchFavoriteProperties()}
                      favoriteIds={favoriteProperties.map(p => p.id)}
                      onLoginRequired={() => { setLoginIntent('favorites'); setAuthModalOpen(true); }}
                    />
                    {reducedProperties.length > 0 && (
                      <PropertyCarousel 
                        title="Obniżka ceny" 
                        properties={reducedProperties}
                        onPropertyClick={handlePropertyClickWithTracking}
                        userId={userId}
                        onFavoritesChange={() => fetchFavoriteProperties()}
                        favoriteIds={favoriteProperties.map(p => p.id)}
                        onLoginRequired={() => { setLoginIntent('favorites'); setAuthModalOpen(true); }}
                      />
                    )}
                    {viewedProperties.length > 0 && (
                      <PropertyCarousel 
                        title="Ostatnio oglądane" 
                        properties={viewedProperties}
                        onPropertyClick={handlePropertyClickWithTracking}
                        userId={userId}
                        onFavoritesChange={() => fetchFavoriteProperties()}
                        favoriteIds={favoriteProperties.map(p => p.id)}
                        onLoginRequired={() => { setLoginIntent('favorites'); setAuthModalOpen(true); }}
                      />
                    )}
                  </>
                );
              })()
            ) : (
              <>
                {recentTenantProfiles.length > 0 && (
                  <TenantProfileCarousel
                    title="Ostatnio dodane" 
                    profiles={recentTenantProfiles}
                    onTenantClick={handleTenantClickWithUrl}
                    userId={userId}
                    onFavoritesChange={() => tenantFavorites.fetchFavoriteTenants()}
                    favoriteIds={tenantFavorites.favoriteTenants.map(t => t.id)}
                    onLoginRequired={() => { setLoginIntent('favorites'); setAuthModalOpen(true); }}
                  />
                )}
                {viewedTenants.length > 0 && (
                  <TenantProfileCarousel 
                    title="Ostatnio oglądane" 
                    profiles={viewedTenants}
                    onTenantClick={handleTenantClickWithUrl}
                    userId={userId}
                    onFavoritesChange={() => tenantFavorites.fetchFavoriteTenants()}
                    favoriteIds={tenantFavorites.favoriteTenants.map(t => t.id)}
                    onLoginRequired={() => { setLoginIntent('favorites'); setAuthModalOpen(true); }}
                  />
                )}
                {recentTenantProfiles.length === 0 && viewedTenants.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, px: 2, width: '100%' }}>
                    <Typography variant="h2" sx={{ fontSize: '3rem', mb: 2 }}>👤</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>Brak najemców</Typography>
                    <Typography variant="body1" sx={{ color: '#777', mb: 3 }}>
                      Nie znaleźliśmy profili pasujących do kryteriów.<br/>Spróbuj zmienić filtry lub dodaj swoje ogłoszenie.<br/>Ustaw filtry, aby opisać swoją nieruchomość.
                    </Typography>
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={() => {
                        if (!isUserLoggedIn) {
                          localStorage.setItem('pendingIntent', 'addListing');
                          setLoginIntent('addListing');
                          setAuthModalOpen(true);
                        } else {
                          setAddListingFormOpen(true);
                        }
                      }}
                      sx={{
                        backgroundColor: '#D2691E',
                        borderRadius: 3,
                        px: 3,
                        py: 1,
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        '&:hover': { backgroundColor: '#B85C00' }
                      }}
                    >
                      + Dodaj ogłoszenie
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Container>

      {/* Footer */}
      <Box 
        sx={{ 
          backgroundColor: 'rgba(0,0,0,0.2)',
          color: 'white',
          py: 4,
          mt: 6
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            {/* Linki wyśrodkowane */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', alignItems: 'center', gap: { xs: 1, sm: 3 }, mb: 2 }}>
              {[
                { label: 'FAQ', href: '/faq' },
                { label: 'Regulamin', href: '/regulamin' },
                { label: 'Polityka prywatności', href: '/polityka-prywatnosci' },
                { label: 'Zgłoś problem', href: '#' },
                { label: 'Kontakt', href: '/kontakt' },
              ].map(({ label: item, href }) => (
                <Typography 
                  key={item}
                  variant="body2" 
                  component="a"
                  href={href}
                  target={href !== '#' ? '_blank' : undefined}
                  rel={href !== '#' ? 'noopener noreferrer' : undefined}
                  sx={{ 
                    opacity: 0.8, 
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      opacity: 1,
                      color: '#D2691E',
                      textDecoration: 'underline'
                    }
                  }}
                  onClick={(e) => {
                    if (item === 'Zgłoś problem') {
                      e.preventDefault();
                      setBugReportOpen(true);
                    }
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
          
          <Box 
            sx={{ 
              textAlign: 'center', 
              pt: 3, 
              mt: 3, 
              borderTop: '1px solid rgba(255,255,255,0.2)' 
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
             Wszelkie prawa zastrzeżone<br />© {new Date().getFullYear()} dobrynajemca.pl 
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Password Recovery Dialog (po kliknięciu linku z emaila) */}
      <RecoveryPasswordDialog 
        open={isRecoveryMode} 
        onClose={() => setIsRecoveryMode(false)} 
      />

      {/* Auth Modal */}
      <AuthModal 
        open={authModalOpen}
        loginIntent={loginIntent}
        onClose={() => {
          setAuthModalOpen(false);
          setLoginIntent(null);
        }}
        onLoginSuccess={(favorites, email, userIdFromModal) => {
          setAuthModalOpen(false);
          setLoginIntent(null);
          handleAuthSuccess(favorites, email, userIdFromModal);
          // Wywołaj ręcznie — useEffect może nie odpalić gdy userId już był ustawiony
          setTimeout(() => handlePendingIntent(), 200);
        }}
      />

      {/* Add Listing Form */}
      <AddListingForm 
        open={addListingFormOpen} 
        onClose={() => {
          setAddListingFormOpen(false);
          editListing.stopEditing();
        }} 
        onAdded={(type) => {
          if (type === 'tenant') {
            setTenantEditProfileData(null);
            handleTenantAdded();
          } else {
            handlePropertyAdded();
          }
        }}
        onUpdated={async (propertyId, updatedData) => {
          await registerPriceChange({
            propertyId,
            userId,
            userEmail,
            oldProperty: editListing.editingProperty,
            updatedData,
          });
          await handlePropertyAdded();
          await fetchUserListings(userId);
          await fetchUserTenantProfiles(userId);
          await fetchReducedProperties();
        }}
        userId={userId}
        userEmail={userEmail}
        editMode={editListing.isEditing}
        editData={editListing.editingProperty}
        tenantEditMode={!!tenantEditProfileData}
        tenantEditData={tenantEditProfileData}
        initialTab={selectedTab === TAB_TENANTS ? 1 : 0}
        isFirstTime={userListings.length === 0 && userTenantProfiles.length === 0}
      />

      {/* Property Details Modal */}
      <PropertyDetailsModal 
        open={propertyDetailsOpen} 
        onClose={() => {
          closePropertyDetails();
          shareableUrl.resetUrl();
          updateCanonical('/');
          document.title = 'Wynajem Mieszkań Bez Agencji - Okazje Cenowe, Profile Najemców, Zero Kosztów.';
          openedFromMyListingsRef.current = false;
          if (openedFromFavoritesRef.current) {
            openedFromFavoritesRef.current = false;
            setFavoritesOpen(true);
          }
        }}
        property={selectedProperty}
        userId={userId}
        userEmail={userEmail}
        isFavorite={selectedProperty && favoriteProperties.some(p => p.id === selectedProperty.id)}
        onFavoritesChange={() => fetchFavoriteProperties()}
        onLoginRequired={(reason) => {
          if (reason === 'message' && selectedProperty) {
            localStorage.setItem('pendingIntent', 'message');
            localStorage.setItem('pendingPropertyId', String(selectedProperty.id));
          }
          setLoginIntent(reason || 'favorites');
          setAuthModalOpen(true);
        }}
        isOwner={!!(userId && selectedProperty && selectedProperty.user_id === userId && openedFromMyListingsRef.current)}
        hideMessageForm={!!(userId && selectedProperty && selectedProperty.user_id === userId && !openedFromMyListingsRef.current)}
        onGoToMyListings={async () => {
          closePropertyDetails();
          openedFromMyListingsRef.current = false;
          await fetchUserListings(userId);
          await fetchUserTenantProfiles(userId);
          setMyListingsOpen(true);
        }}
        onEdit={async (property) => {
          setEditOrigin('details');
          setEditedPropertyId(property.id);
          closePropertyDetails();
          await editListing.startEditing(property.id);
          setAddListingFormOpen(true);
        }}
      />

      {/* Tenant Details Modal */}
      <TenantDetailsModal 
        open={tenantDetailsOpen} 
        onClose={() => {
          closeTenantDetails();
          shareableUrl.resetUrl();
          updateCanonical('/');
          document.title = 'Wynajem Mieszkań Bez Agencji - Okazje Cenowe, Profile Najemców, Zero Kosztów.';
          setTenantDetailsIsOwner(false);
          if (openedFromFavoritesRef.current) {
            openedFromFavoritesRef.current = false;
            setFavoritesOpen(true);
          }
        }}
        tenant={selectedTenant}
        viewOnly={true}
        isOwner={tenantDetailsIsOwner}
        hideMessageForm={!!(userId && selectedTenant && selectedTenant.user_id === userId && !tenantDetailsIsOwner)}
        userEmail={userEmail}
        onEditProfile={(profile) => {
          closeTenantDetails();
          setTenantDetailsIsOwner(false);
          setTenantEditProfileData(profile);
          setAddListingFormOpen(true);
        }}
        onGoToMyListings={async () => {
          closeTenantDetails();
          setTenantDetailsIsOwner(false);
          await fetchUserListings(userId);
          await fetchUserTenantProfiles(userId);
          setMyListingsOpen(true);
        }}
        onGoToMessages={() => {
          closeTenantDetails();
          setTenantDetailsIsOwner(false);
          setMessagesInboxOpen(true);
        }}
        userId={userId}
        userListings={userListings}
        fetchUserListings={() => fetchUserListings(userId)}
        isFavorite={selectedTenant && tenantFavorites.favoriteTenants.some(t => t.id === selectedTenant.id)}
        onFavoritesChange={() => tenantFavorites.fetchFavoriteTenants()}
        onLoginRequired={(reason) => { setLoginIntent(reason || 'favorites'); setAuthModalOpen(true); }}
      />

      {/* Modal ulubionych nieruchomości */}
      <Modal
        open={favoritesOpen}
        onClose={handleFavoritesClose}
        aria-labelledby="favorites-modal-title"
        aria-describedby="favorites-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', md: 700 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 4,
          p: 4,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography id="favorites-modal-title" variant="h5" fontWeight="bold">
              Ulubione
            </Typography>
            <IconButton onClick={handleFavoritesClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Pusty stan — gdy obie listy są puste */}
          {favoriteProperties.length === 0 && tenantFavorites.favoriteTenants.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
              <FavoriteIcon sx={{ fontSize: 64, color: '#E2C4B0' }} />
              <Typography variant="h6" sx={{ color: '#9CA3AF', fontWeight: 500 }}>
                Nie masz jeszcze nic w ulubionych
              </Typography>
            </Box>
          ) : (
            <>
              {/* Ulubione nieruchomości */}
              {favoriteProperties.length > 0 && (
                <>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#2D3748' }}>
                    Nieruchomości
                  </Typography>
                  <Listing
                    properties={favoriteProperties.map(property => ({
                      ...property,
                      onRemoveFavorite: handleRemoveFavorite
                    }))}
                    onPropertyClick={property => {
                      openedFromFavoritesRef.current = true;
                      handlePropertyClickWithTracking(property);
                      setFavoritesOpen(false);
                    }}
                  />
                </>
              )}

              {/* Ulubieni najemcy */}
              {tenantFavorites.favoriteTenants.length > 0 && (
                <>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: favoriteProperties.length > 0 ? 3 : 0, mb: 1, color: '#2D3748' }}>
                    Najemcy
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {tenantFavorites.favoriteTenants.map(tenant => (
                      <Card key={tenant.id} onClick={() => { openedFromFavoritesRef.current = true; handleTenantClickWithUrl(tenant); setFavoritesOpen(false); }} sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', cursor: 'pointer', '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)' } }}>
                        <Avatar
                          src={tenant.profile_photo_url}
                          sx={{ width: 56, height: 56, mr: 2 }}
                        >
                          <PersonIcon />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {tenant.first_name || tenant.name || 'Najemca'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {tenant.city || ''} {tenant.preferred_district ? `• ${tenant.preferred_district}` : ''}
                          </Typography>
                        </Box>
                        <RemoveFavoriteButton onClick={async () => {
                          await tenantFavorites.removeTenantFromFavorites(tenant.id);
                          await tenantFavorites.fetchFavoriteTenants();
                        }} />
                      </Card>
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Modal>

      {/* Modal "Moje ogłoszenia" */}
      <Modal
        open={myListingsOpen}
        onClose={() => setMyListingsOpen(false)}
        aria-labelledby="my-listings-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', md: 700 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 4,
          p: 4,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography id="my-listings-modal-title" variant="h5" fontWeight="bold">
              Moje ogłoszenia
            </Typography>
            <IconButton onClick={() => setMyListingsOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {/* Sekcja: Mój profil najemcy */}
          {userTenantProfiles.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ color: '#D2691E' }} /> Mój profil najemcy
              </Typography>
              {userTenantProfiles.map(profile => (
                <Box key={profile.id} sx={{
                  p: 2, mb: 1, borderRadius: 2, border: '1px solid #FFE4CC',
                  backgroundColor: '#FFF8F3',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={profile.avatar} sx={{ width: 48, height: 48, bgcolor: '#FFD7B5' }}>
                        {!profile.avatar && <PersonIcon />}
                      </Avatar>
                      <Box>
                        <Typography fontWeight="bold">{profile.name || 'Profil najemcy'}</Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {[profile.city, profile.roomsWanted ? `${profile.roomsWanted} pok.` : null, profile.priceRange].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => { setTenantDetailsIsOwner(true); handleTenantClickWithUrl(profile); setMyListingsOpen(false); }}
                        sx={{ textTransform: 'none', borderColor: '#D2691E', color: '#D2691E', '&:hover': { borderColor: '#B85C00', backgroundColor: 'rgba(210,105,30,0.05)' } }}
                      >
                        Podgląd
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => setDeletingTenantId(profile.id)}
                        sx={{ color: '#EF4444', '&:hover': { backgroundColor: 'rgba(239,68,68,0.1)' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  {deletingTenantId === profile.id && (
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1.5,
                        px: 1.5,
                        py: 1,
                        backgroundColor: '#FEF2F2',
                        borderRadius: 1.5,
                        border: '1px solid #FECACA',
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: '#DC2626', fontWeight: 600 }}>
                        Usunąć profil?
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setDeletingTenantId(null)}
                          sx={{
                            minWidth: 'auto',
                            px: 1.5,
                            py: 0,
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#6B7280',
                            borderColor: '#D1D5DB',
                            borderRadius: 1,
                            textTransform: 'none',
                            lineHeight: 1.8,
                            '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' }
                          }}
                        >
                          Anuluj
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={async () => {
                            try {
                              const { tenantsService } = await import('./services/supabaseService');
                              await tenantsService.deleteById(profile.id, userId);
                              setDeletingTenantId(null);
                              await fetchUserTenantProfiles(userId);
                              await fetchInitialTenants();
                            } catch (err) {
                              alert('Błąd podczas usuwania: ' + err.message);
                            }
                          }}
                          sx={{
                            minWidth: 'auto',
                            px: 1.5,
                            py: 0,
                            fontSize: 11,
                            fontWeight: 600,
                            backgroundColor: '#EF4444',
                            borderRadius: 1,
                            textTransform: 'none',
                            lineHeight: 1.8,
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#DC2626', boxShadow: 'none' }
                          }}
                        >
                          Usuń
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
              <Divider sx={{ mt: 2, mb: 2 }} />
            </Box>
          )}

          {/* Sekcja: Ogłoszenia nieruchomości */}
          {userListings.length === 0 && userTenantProfiles.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <HomeIcon sx={{ fontSize: 64, color: '#FFD7B5', mb: 2 }} />
              <Typography variant="body1" sx={{ color: '#666', mt: 2 }}>
                Nie masz jeszcze żadnych ogłoszeń.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (userId) {
                    setMyListingsOpen(false);
                    setAddListingFormOpen(true);
                  } else {
                    setMyListingsOpen(false);
                    localStorage.setItem('pendingIntent', 'addListing');
                    setLoginIntent('addListing');
                    setAuthModalOpen(true);
                  }
                }}
                sx={{ 
                  mt: 3,
                  backgroundColor: '#FB923C',
                  '&:hover': { backgroundColor: '#EA580C' },
                  textTransform: 'none',
                }}
              >
                Dodaj pierwsze ogłoszenie
              </Button>
            </Box>
          ) : userListings.length > 0 ? (
            <>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HomeIcon sx={{ color: '#D2691E' }} /> Moje ogłoszenia nieruchomości
            </Typography>
            <Listing
              properties={userListings.map(property => ({
                ...property,
                image_url: Array.isArray(property.image_url) ? property.image_url : [property.image_url],
                title: property.title || `${property.city || ''} ${property.district || ''}`.trim(),
                city: property.city,
                street: property.street,
                district: property.district,
                address: [property.street, property.building_number, property.city, property.district].filter(Boolean).join(', '),
                created_at: property.created_at,
                renderAction: () => (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await editListing.startEditing(property.id);
                          setEditOrigin('thumbnails');
                          setMyListingsOpen(false);
                          setAddListingFormOpen(true);
                        } catch (err) {}
                      }}
                      sx={{
                        color: '#FB923C',
                        width: 32,
                        height: 32,
                        ml: 0.5,
                        '&:hover': { backgroundColor: 'rgba(251, 146, 60, 0.1)' }
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingPropertyId(property.id);
                      }}
                      sx={{
                        color: '#EF4444',
                        width: 32,
                        height: 32,
                        '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' }
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ),
                renderBottomAction: deletingPropertyId === property.id ? () => (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 1,
                      px: 1.5,
                      py: 1,
                      backgroundColor: '#FEF2F2',
                      borderRadius: 1.5,
                      border: '1px solid #FECACA',
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: '#DC2626', fontWeight: 600 }}>
                      Usunąć?
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingPropertyId(null);
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 1.5,
                          py: 0,
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#6B7280',
                          borderColor: '#D1D5DB',
                          borderRadius: 1,
                          textTransform: 'none',
                          lineHeight: 1.8,
                          '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' }
                        }}
                      >
                        Anuluj
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleDeleteListing(property.id, userId);
                          setDeletingPropertyId(null);
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 1.5,
                          py: 0,
                          fontSize: 11,
                          fontWeight: 600,
                          backgroundColor: '#EF4444',
                          borderRadius: 1,
                          textTransform: 'none',
                          lineHeight: 1.8,
                          boxShadow: 'none',
                          '&:hover': { backgroundColor: '#DC2626', boxShadow: 'none' }
                        }}
                      >
                        Usuń
                      </Button>
                    </Box>
                  </Box>
                ) : undefined
              }))}
              onPropertyClick={property => {
                openedFromMyListingsRef.current = true;
                handlePropertyClickWithTracking(property);
                setMyListingsOpen(false);
              }}
              showEditButton={false}
            />
            </>
          ) : null}
        </Box>
      </Modal>

      {/* Settings Modal */}
      <Dialog
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Ustawienia konta
            </Typography>
            <IconButton onClick={() => setSettingsModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>

            {/* Zmiana hasła */}
            <Box sx={{ border: '1px solid #f0ece8', borderRadius: 2, p: 2 }}>
              <PasswordChangeForm userEmail={userEmail} />
            </Box>

            {/* Usuń konto */}
            <Box sx={{ border: '1px solid #fee2e2', borderRadius: 2, p: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                color="error"
                sx={{ py: 1.5, textTransform: 'none', fontSize: '14px', borderColor: '#fca5a5', '&:hover': { borderColor: '#ef4444', backgroundColor: '#fff5f5' } }}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Usuń konto
              </Button>
            </Box>

            {/* Powiadomienia */}
            <Box sx={{ border: '1px solid #f0ece8', borderRadius: 2, p: 2 }}>
              <NotificationPreferences userId={userId} supabase={supabase} />
            </Box>

          </Box>
        </DialogContent>
      </Dialog>

      {/* Custom Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: '#D2691E', 
          fontWeight: 'bold',
          borderBottom: '1px solid #eee'
        }}>
          Potwierdzenie usunięcia konta
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Czy na pewno chcesz usunąć swoje konto?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Twoje ogłoszenia i profile najemców zostaną całkowicie usunięte.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Nie
          </Button>
          <Button 
            onClick={() => handleDeleteAccount({
              fetchInitialProperties,
              fetchInitialTenants,
              setRecentProperties,
              setRecentTenantProfiles,
              setVerifiedTenantProfiles,
              setPremiumTenantProfiles
            })}
            variant="contained"
            color="error"
            sx={{ minWidth: 100 }}
          >
            Tak, usuń
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={deleteSuccessOpen}
        onClose={() => setDeleteSuccessOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: '#4CAF50', 
          fontWeight: 'bold',
          borderBottom: '1px solid #eee'
        }}>
          Konto zostało dezaktywowane
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1">
            Twoje konto zostało dezaktywowane, a wszystkie ogłoszenia i profile najemców zostały usunięte.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDeleteSuccessOpen(false)}
            variant="contained"
            color="primary"
            sx={{ minWidth: 100 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* Skrzynka odbiorcza wiadomości */}
      <MessagesInbox
        open={messagesInboxOpen}
        onClose={() => { setMessagesInboxOpen(false); unreadMessages.refresh(); }}
        userEmail={userEmail}
        userId={userId}
        onUnreadChange={() => unreadMessages.refresh()}
      />

      {/* Widget zgłaszania usterek */}
      <BugReportWidget userEmail={userEmail} open={bugReportOpen} onClose={() => setBugReportOpen(false)} />

      {/* Komunikat powitalny po potwierdzeniu emaila */}
      <Snackbar
        open={welcomeSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setWelcomeSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setWelcomeSnackbarOpen(false)}
          severity="success"
          sx={{ fontSize: '15px', fontWeight: 500 }}
        >
          🎉 Konto aktywowane! Witaj na dobrynajemca.pl — możesz teraz przeglądać ogłoszenia.
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Update <link rel="canonical"> to match current URL path
function updateCanonical(path) {
  const el = document.getElementById('canonical-url');
  if (el) el.href = 'https://dobrynajemca.pl' + (path || '/');
}

// Main App component with Context Provider
function App() {
  const pathname = window.location.pathname;
  const isKnownPath = pathname === '/' || pathname === '' || pathname.startsWith('/m/') || pathname.startsWith('/n/');

  useEffect(() => {
    const onPopState = () => updateCanonical(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  if (!isKnownPath) {
    return <NotFound onGoHome={() => { window.location.href = '/'; }} />;
  }

  return (
    <ErrorBoundary>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
